export type MoneyPageId = 'stickers' | 'canvas' | 'borden' | 'kerst' | 'bouw' | 'inhaker' | 'drukwerk' | 'beurs';

export type MoneyPageCopy = {
  pathKey: '/stickers' | '/foto-op-canvas' | '/reclameborden' | '/kerstkaarten' | '/bouwborden' | '/inhaker-systeem' | '/drukwerk' | '/beurs-en-evenement';
  metaTitle: string;
  metaDescription: string;
  badge: string;
  title: string;
  subtitle: string;
  primaryCta: string;
  primaryHref: string;
  support: string;
  usps: Array<[string, string]>;
  productsTitle: string;
  products: Array<{ slug: string; label: string; text: string }>;
  faqTitle: string;
  faqs: Array<{ q: string; a: string }>;
  relatedTitle: string;
  related: Array<{ id: MoneyPageId; label: string }>;
};

const pages: Record<MoneyPageId, Record<'nl' | 'de' | 'en' | 'fr', MoneyPageCopy>> = {
  stickers: {
    de: {
      pathKey: '/stickers',
      metaTitle: 'Aufkleber drucken lassen | Vinyl, Auto, Fenster | Printen.eu',
      metaDescription: 'Aufkleber nach Maß in Deutschland bestellen. Vinyl, Auto, Fenster, Rolle oder Konturschnitt. Dateiprüfung inklusive und Lieferung nach Deutschland.',
      badge: 'Druck aus den Niederlanden, Lieferung nach Deutschland',
      title: 'Aufkleber drucken, die draußen halten',
      subtitle: 'Wählen Sie Material und Form, laden Sie Ihr Motiv hoch und bestellen Sie direkt. Wir prüfen die Datei vor dem Druck und liefern nach Deutschland.',
      primaryCta: 'Vinylaufkleber konfigurieren',
      primaryHref: '/producten/vinylsticker',
      support: 'Fragen zu Datei oder Bestellung? Unser Kundenservice hilft Ihnen per E-Mail weiter.',
      usps: [
        ['Kostenlose Dateiprüfung', 'Zu wenig DPI oder falscher Beschnitt fällt auf, bevor wir drucken.'],
        ['Material nach Einsatz', 'Innen, außen, Auto, Fenster oder Rolle: Sie wählen nach Anwendung, nicht nach Jargon.'],
        ['Rechnung mit MwSt.', 'Privat und geschäftlich. Deutsche MwSt. über OSS, klare Rechnung.'],
        ['Nachdruck bei Druckfehler', 'Produktionsfehler oder Transportschaden lösen wir mit Nachdruck, festgehalten in der Bestellung.'],
      ],
      productsTitle: 'Häufig in Deutschland bestellt',
      products: [
        { slug: 'vinylsticker', label: 'Vinylaufkleber', text: 'Allround für Logo, Aktion und Außeneinsatz.' },
        { slug: 'autosticker', label: 'Autoaufkleber', text: 'Für Pkw, Transporter und Fuhrpark.' },
        { slug: 'raamstickers', label: 'Fensteraufkleber', text: 'Schaufenster, Öffnungszeiten, Sichtschutz.' },
        { slug: 'stickervellen', label: 'Etikettenbogen', text: 'Verpackung, Versand und Produktlabels.' },
        { slug: 'kiss-cut-stickers-met-print-673', label: 'Konturschnitt', text: 'Aufkleber in der Form Ihres Motivs.' },
        { slug: 'muursticker', label: 'Wandtattoo', text: 'Innenraum, Büro und Kinderzimmer.' },
      ],
      faqTitle: 'Häufige Fragen zu Aufklebern',
      faqs: [
        { q: 'Halten die Aufkleber draußen?', a: 'Ja, Vinyl für außen ist wetterfest. Für Autos und Fassaden wählen Sie das passende Material in der Konfiguration.' },
        { q: 'Welche Produktions- und Versandoptionen gibt es für Deutschland?', a: 'Die verfügbaren Optionen hängen vom Produkt und von Ihrer Konfiguration ab. Versandkosten werden im Warenkorb beziehungsweise an der Kasse berechnet.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Bestellen Sie auf Firmennamen. Innerhalb der EU mit gültiger USt-IdNr. gilt die Umkehrung der Steuerschuld.' },
        { q: 'Was, wenn der Druck fehlerhaft ist?', a: 'Mailen Sie Fotos und Bestellnummer an support@printen.eu. Bei Produktionsfehler oder Transportschaden gibt es Nachdruck oder Erstattung.' },
        { q: 'Gibt es ein Widerrufsrecht?', a: '14 Tage bei Standardprodukten. Personalisierte Drucke nach Ihrer Datei sind ausgenommen, außer bei Fehler oder Schaden.' },
        { q: 'Welche Datei brauche ich?', a: 'PDF, PNG oder JPG, möglichst 300 dpi in Endformat. Wir prüfen vor dem Druck und melden uns bei Problemen.' },
      ],
      relatedTitle: 'Weiter für Deutschland',
      related: [
        { id: 'canvas', label: 'Foto auf Leinwand' },
        { id: 'borden', label: 'Werbeschilder' },
      ],
    },
    nl: {
      pathKey: '/stickers',
      metaTitle: 'Stickers bestellen op maat | Vinyl, auto, raam | Printen.eu',
      metaDescription: 'Stickers op maat: vinyl, auto, raam, rol of contour. Bestandscheck inbegrepen, levering in NL, BE en DE.',
      badge: 'Gratis bestandscontrole',
      title: 'Stickers die blijven zitten',
      subtitle: 'Kies materiaal en vorm, upload je ontwerp en bestel. Wij checken het bestand voor druk.',
      primaryCta: 'Vinylsticker configureren',
      primaryHref: '/producten/vinylsticker',
      support: 'Klantenservice: antwoord binnen 1 werkdag, kantooruren 09:00-17:00.',
      usps: [
        ['Gratis bestandscheck', 'Te weinig DPI of verkeerde snijlijn vangen we vóór druk.'],
        ['Materiaal per toepassing', 'Binnen, buiten, auto, raam of rol.'],
        ['Btw-factuur', 'Particulier en zakelijk, OSS in DE.'],
        ['Herdruk bij drukfout', 'Productiefout of transportschade lossen we op met herdruk.'],
      ],
      productsTitle: 'Vaak besteld',
      products: [
        { slug: 'vinylsticker', label: 'Vinylstickers', text: 'Allround voor logo, actie en buiten.' },
        { slug: 'autosticker', label: 'Autostickers', text: 'Voor auto, bus en wagenpark.' },
        { slug: 'raamstickers', label: 'Raamstickers', text: 'Winkelruit, openingstijden, privacy.' },
        { slug: 'stickervellen', label: 'Stickervellen', text: 'Verpakking, verzending, labels.' },
        { slug: 'kiss-cut-stickers-met-print-673', label: 'Eigen vorm', text: 'Contour rondom je ontwerp.' },
        { slug: 'muursticker', label: 'Muurstickers', text: 'Interieur, kantoor, kinderkamer.' },
      ],
      faqTitle: 'Veelgestelde vragen over stickers',
      faqs: [
        { q: 'Blijven stickers buiten zitten?', a: 'Vinyl voor buiten is weerbestendig. Kies het juiste materiaal in de configurator.' },
        { q: 'Welke productie- en verzendopties zijn er voor Duitsland?', a: 'De beschikbare opties hangen af van het product en de gekozen configuratie. Verzendkosten worden in de winkelwagen of checkout berekend.' },
        { q: 'Kan ik zakelijk bestellen?', a: 'Ja, op bedrijfsnaam. Met geldig EU-btw-nummer geldt verlegging.' },
        { q: 'Wat bij een drukfout?', a: 'Mail foto en ordernummer. Bij productiefout of schade volgt herdruk of terugbetaling.' },
        { q: 'Geldt herroepingsrecht?', a: '14 dagen bij standaardproducten. Gepersonaliseerd drukwerk niet, behalve bij fout of schade.' },
        { q: 'Welk bestand heb ik nodig?', a: 'PDF, PNG of JPG, bij voorkeur 300 dpi op eindformaat. Wij checken voor druk.' },
      ],
      relatedTitle: 'Ook bekeken',
      related: [
        { id: 'canvas', label: 'Foto op canvas' },
        { id: 'borden', label: 'Reclameborden' },
      ],
    },
    en: {
      pathKey: '/stickers',
      metaTitle: 'Custom stickers | Vinyl, car, window | Printen.eu',
      metaDescription: 'Order custom stickers: vinyl, car, window, roll or contour cut. File check included. Delivery to DE, NL, BE and FR.',
      badge: 'Free file check',
      title: 'Stickers that stay put',
      subtitle: 'Pick material and shape, upload your artwork and order. We check the file before print.',
      primaryCta: 'Configure vinyl stickers',
      primaryHref: '/producten/vinylsticker',
      support: 'Support: reply within 1 business day, office hours 09:00-17:00.',
      usps: [
        ['Free file check', 'Low DPI or a bad cut line is caught before print.'],
        ['Material by use', 'Indoor, outdoor, car, window or roll.'],
        ['VAT invoice', 'Consumer and business, OSS VAT in Germany.'],
        ['Reprint on print error', 'Production faults or transit damage are fixed with a reprint.'],
      ],
      productsTitle: 'Often ordered',
      products: [
        { slug: 'vinylsticker', label: 'Vinyl stickers', text: 'All-round for logo, promo and outdoor use.' },
        { slug: 'autosticker', label: 'Car stickers', text: 'For cars, vans and fleets.' },
        { slug: 'raamstickers', label: 'Window stickers', text: 'Shop windows, hours, privacy.' },
        { slug: 'stickervellen', label: 'Sticker sheets', text: 'Packaging, shipping, labels.' },
        { slug: 'kiss-cut-stickers-met-print-673', label: 'Custom shape', text: 'Cut around your artwork.' },
        { slug: 'muursticker', label: 'Wall stickers', text: 'Interiors, offices, kids rooms.' },
      ],
      faqTitle: 'Sticker FAQ',
      faqs: [
        { q: 'Do they last outdoors?', a: 'Outdoor vinyl is weatherproof. Pick the right material in the configurator.' },
        { q: 'Which production and shipping options are available for Germany?', a: 'Available options depend on the product and selected configuration. Shipping costs are calculated in the cart or at checkout.' },
        { q: 'Can I order as a business?', a: 'Yes. With a valid EU VAT number, reverse charge applies.' },
        { q: 'What if the print is wrong?', a: 'Email a photo and order number. Production faults or damage get a reprint or refund.' },
        { q: 'Is there a cooling-off period?', a: '14 days for standard products. Personalised prints are excluded except for faults or damage.' },
        { q: 'Which file do I need?', a: 'PDF, PNG or JPG, ideally 300 dpi at final size. We check before print.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'canvas', label: 'Photo on canvas' },
        { id: 'borden', label: 'Sign boards' },
      ],
    },
    fr: {
      pathKey: '/stickers',
      metaTitle: 'Stickers sur mesure | Vinyle, auto, vitrine | Printen.eu',
      metaDescription: 'Commandez des stickers sur mesure. Contrôle de fichier inclus, livraison en DE, NL, BE et FR.',
      badge: 'Controle de fichier gratuit',
      title: 'Des stickers qui tiennent',
      subtitle: 'Choisissez le materiau et la forme, importez votre visuel et commandez. Nous verifions le fichier avant impression.',
      primaryCta: 'Configurer un sticker vinyle',
      primaryHref: '/producten/vinylsticker',
      support: 'Support: reponse sous 1 jour ouvre, horaires 09:00-17:00.',
      usps: [
        ['Controle de fichier', 'DPI trop bas ou fond perdu incorrect: on le voit avant impression.'],
        ['Materiau selon usage', 'Interieur, exterieur, auto, vitrine ou rouleau.'],
        ['Facture TVA', 'Particulier et pro, TVA OSS en Allemagne.'],
        ['Reimpression si erreur', 'Defaut de production ou casse transport: reimpression.'],
      ],
      productsTitle: 'Souvent commandes',
      products: [
        { slug: 'vinylsticker', label: 'Stickers vinyle', text: 'Polyvalent logo, promo, exterieur.' },
        { slug: 'autosticker', label: 'Stickers auto', text: 'Voiture, utilitaire, flotte.' },
        { slug: 'raamstickers', label: 'Stickers vitrine', text: 'Horaires, promo, intimite.' },
        { slug: 'stickervellen', label: 'Planches', text: 'Emballage, expedition, etiquettes.' },
        { slug: 'kiss-cut-stickers-met-print-673', label: 'Forme libre', text: 'Decoupe autour du visuel.' },
        { slug: 'muursticker', label: 'Stickers muraux', text: 'Interieur, bureau, chambre.' },
      ],
      faqTitle: 'Questions frequentes',
      faqs: [
        { q: 'Tiennent-ils dehors?', a: 'Le vinyle exterieur resiste aux intemperies. Choisissez le bon materiau.' },
        { q: 'Delai vers l Allemagne?', a: 'Le délai figure sur le produit. Express possible. Nous mesurons promesse et livraison.' },
        { q: 'Commande pro?', a: 'Oui. Avec un numero de TVA UE valide, autoliquidation.' },
        { q: 'Erreur d impression?', a: 'Envoyez photo et numero. Defaut ou casse: reimpression ou remboursement.' },
        { q: 'Droit de retractation?', a: '14 jours pour le standard. Le personnalisé est exclu, sauf défaut ou casse.' },
        { q: 'Quel fichier?', a: 'PDF, PNG ou JPG, idealement 300 dpi au format final. Nous controlons avant impression.' },
      ],
      relatedTitle: 'A voir aussi',
      related: [
        { id: 'canvas', label: 'Photo sur toile' },
        { id: 'borden', label: 'Panneaux pub' },
      ],
    },
  },
  canvas: {
    de: {
      pathKey: '/foto-op-canvas',
      metaTitle: 'Foto auf Leinwand drucken | Canvas nach Maß | Printen.eu',
      metaDescription: 'Leinwand mit eigenem Foto bestellen. Format wählen, Bild hochladen, Dateiprüfung inklusive und Lieferung nach Deutschland.',
      badge: 'Foto wird vor dem Druck geprüft',
      title: 'Ihr Foto als Leinwand, scharf an der Wand',
      subtitle: 'Format wählen, Foto hochladen, bestellen. Wir prüfen die Auflösung. Lieferung nach DE, Nachdruck bei Druckfehler.',
      primaryCta: 'Leinwand konfigurieren',
      primaryHref: '/producten/canvas',
      support: 'Fragen zu Foto, Datei oder Bestellung? Unser Kundenservice hilft Ihnen per E-Mail weiter.',
      usps: [
        ['Schärfeprüfung', 'Zu kleine Dateien sehen Sie vor dem Kauf. Kein unscharfes Überraschungspaket.'],
        ['Rahmenfertig', 'Canvas kommt auf Keilrahmen, bereit zum Aufhängen.'],
        ['Faire DE-MwSt.', 'OSS: deutsche Mehrwertsteuer auf der Rechnung, kein Rätsel.'],
        ['Geschenk ohne Risiko', 'Druckfehler oder Transportschaden: Nachdruck, fest in der Bestellung.'],
      ],
      productsTitle: 'Leinwand und Wand',
      products: [
        { slug: 'canvas', label: 'Foto auf Leinwand', text: 'Klassiker für Wohnzimmer, Büro und Geschenk.' },
        { slug: 'fotopaneel-met-baklijst', label: 'Fotopanel mit Schattenfuge', text: 'Galerie-Look mit schwebendem Rahmen.' },
        { slug: 'foto-op-poster', label: 'Foto als Poster', text: 'Günstiger, flach, gut für große Formate.' },
      ],
      faqTitle: 'Häufige Fragen zur Leinwand',
      faqs: [
        { q: 'Wie groß darf das Foto sein?', a: 'Je größer die Leinwand, desto mehr Pixel brauchen Sie. Der DPI-Hinweis steht am Produkt. Unsicher: Datei hochladen, wir prüfen.' },
        { q: 'Kommt die Leinwand aufgespannt?', a: 'Ja, auf Keilrahmen, versandfertig zum Aufhängen.' },
        { q: 'Lieferung nach Deutschland?', a: 'Ja. Lieferzeit und Versandkosten sehen Sie an der Kasse für DE.' },
        { q: 'Kann ich umtauschen?', a: 'Personalisierte Fotodrucke sind vom Widerruf ausgenommen. Bei Fehler oder Schaden drucken wir neu.' },
        { q: 'Welches Format wirkt gut?', a: 'Über dem Sofa oft 90 x 60 oder 120 x 80 cm. Quadratisch für Porträts. Im Konfigurator sehen Sie den Preis live.' },
      ],
      relatedTitle: 'Weiter für Deutschland',
      related: [
        { id: 'stickers', label: 'Aufkleber' },
        { id: 'borden', label: 'Werbeschilder' },
      ],
    },
    nl: {
      pathKey: '/foto-op-canvas',
      metaTitle: 'Foto op canvas bestellen | Printen.eu',
      metaDescription: 'Canvas met eigen foto. Formaat kiezen, beeld uploaden, bestandscheck inbegrepen. Levering NL, BE, DE.',
      badge: 'Scherptecheck voor druk',
      title: 'Jouw foto als canvas, scherp aan de muur',
      subtitle: 'Kies formaat, upload je foto, bestel. Wij checken de resolutie.',
      primaryCta: 'Canvas configureren',
      primaryHref: '/producten/canvas',
      support: 'Klantenservice: antwoord binnen 1 werkdag, kantooruren 09:00-17:00.',
      usps: [
        ['Scherptecheck', 'Te kleine bestanden zie je vóór je betaalt.'],
        ['Klaar om op te hangen', 'Canvas op spieraam.'],
        ['Duidelijke btw', 'OSS: Duitse btw op de factuur bij levering in DE.'],
        ['Herdruk bij fout', 'Drukfout of schade: nieuwe print.'],
      ],
      productsTitle: 'Canvas en wand',
      products: [
        { slug: 'canvas', label: 'Foto op canvas', text: 'Klassieker voor woonkamer, kantoor, cadeau.' },
        { slug: 'fotopaneel-met-baklijst', label: 'Fotopaneel met baklijst', text: 'Galerij-look.' },
        { slug: 'foto-op-poster', label: 'Foto op poster', text: 'Goedkoper, plat, grote formaten.' },
      ],
      faqTitle: 'Veelgestelde vragen over canvas',
      faqs: [
        { q: 'Hoe groot mag de foto zijn?', a: 'Hoe groter het canvas, hoe meer pixels. DPI-hint staat op het product.' },
        { q: 'Komt het opgespannen?', a: 'Ja, op spieraam, klaar om op te hangen.' },
        { q: 'Levering in Duitsland?', a: 'Ja. Levertijd en verzendkosten zie je in de kassa voor DE.' },
        { q: 'Kan ik ruilen?', a: 'Gepersonaliseerde fotoprints vallen buiten herroeping. Bij fout of schade drukken we opnieuw.' },
        { q: 'Welk formaat werkt?', a: 'Boven de bank vaak 90 x 60 of 120 x 80 cm. Vierkant voor portretten.' },
      ],
      relatedTitle: 'Ook bekeken',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'borden', label: 'Reclameborden' },
      ],
    },
    en: {
      pathKey: '/foto-op-canvas',
      metaTitle: 'Photo on canvas | Printen.eu',
      metaDescription: 'Order a photo on canvas. Choose size, upload your image, file check included. Delivery to Germany and the EU.',
      badge: 'Sharpness check before print',
      title: 'Your photo on canvas, sharp on the wall',
      subtitle: 'Pick a size, upload your photo, order. We check resolution.',
      primaryCta: 'Configure canvas',
      primaryHref: '/producten/canvas',
      support: 'Support: reply within 1 business day, office hours 09:00-17:00.',
      usps: [
        ['Sharpness check', 'Files that are too small show up before you pay.'],
        ['Ready to hang', 'Canvas on a stretcher frame.'],
        ['Clear German VAT', 'OSS VAT on the invoice for DE delivery.'],
        ['Reprint on fault', 'Print error or damage: we print again.'],
      ],
      productsTitle: 'Canvas and wall',
      products: [
        { slug: 'canvas', label: 'Photo on canvas', text: 'Classic for living room, office, gift.' },
        { slug: 'fotopaneel-met-baklijst', label: 'Photo panel with float frame', text: 'Gallery look.' },
        { slug: 'foto-op-poster', label: 'Photo poster', text: 'Cheaper, flat, large sizes.' },
      ],
      faqTitle: 'Canvas FAQ',
      faqs: [
        { q: 'How large can the photo be?', a: 'Larger canvas needs more pixels. The DPI hint is on the product.' },
        { q: 'Does it arrive stretched?', a: 'Yes, on a stretcher frame, ready to hang.' },
        { q: 'Delivery to Germany?', a: 'Yes. Lead time and shipping show at checkout for DE.' },
        { q: 'Can I return it?', a: 'Personalised photo prints are excluded from cooling-off. Faults or damage get a reprint.' },
        { q: 'Which size works?', a: 'Above a sofa often 90 x 60 or 120 x 80 cm. Square for portraits.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'borden', label: 'Sign boards' },
      ],
    },
    fr: {
      pathKey: '/foto-op-canvas',
      metaTitle: 'Photo sur toile | Printen.eu',
      metaDescription: 'Toile photo sur mesure. Contrôle de fichier inclus, livraison en Allemagne et UE.',
      badge: 'Controle de nettete avant impression',
      title: 'Votre photo sur toile, nette au mur',
      subtitle: 'Choisissez le format, importez la photo, commandez. Nous verifions la resolution.',
      primaryCta: 'Configurer la toile',
      primaryHref: '/producten/canvas',
      support: 'Support: reponse sous 1 jour ouvre, horaires 09:00-17:00.',
      usps: [
        ['Controle de nettete', 'Un fichier trop petit se voit avant le paiement.'],
        ['Pret a accrocher', 'Toile sur chassis.'],
        ['TVA allemande claire', 'TVA OSS sur la facture en DE.'],
        ['Reimpression si defaut', 'Erreur ou casse: on reimprime.'],
      ],
      productsTitle: 'Toile et mur',
      products: [
        { slug: 'canvas', label: 'Photo sur toile', text: 'Classique salon, bureau, cadeau.' },
        { slug: 'fotopaneel-met-baklijst', label: 'Panneau avec caisse americaine', text: 'Look galerie.' },
        { slug: 'foto-op-poster', label: 'Poster photo', text: 'Moins cher, plat, grands formats.' },
      ],
      faqTitle: 'Questions toile',
      faqs: [
        { q: 'Quelle taille de photo?', a: 'Plus la toile est grande, plus il faut de pixels. L’indicateur DPI est sur le produit.' },
        { q: 'Arrivee tendue?', a: 'Oui, sur chassis, pret a accrocher.' },
        { q: 'Livraison en Allemagne?', a: 'Oui. Delai et port a la caisse pour DE.' },
        { q: 'Retour possible?', a: 'Le photo personnalise est hors retractation. Defaut ou casse: reimpression.' },
        { q: 'Quel format?', a: 'Au-dessus du canapé souvent 90 x 60 ou 120 x 80 cm. Carre pour un portrait.' },
      ],
      relatedTitle: 'A voir aussi',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'borden', label: 'Panneaux' },
      ],
    },
  },
  borden: {
    de: {
      pathKey: '/reclameborden',
      metaTitle: 'Werbeschilder drucken | Dibond, Forex, Fassade | Printen.eu',
      metaDescription: 'Schilder nach Maß: Dibond, Forex, Fassade, Makler oder Gehweg. Dateiprüfung inklusive und Lieferung nach Deutschland.',
      badge: 'Material nach Einsatzort',
      title: 'Werbeschilder, die draußen lesbar bleiben',
      subtitle: 'Dibond, Forex oder Alu-Verbund. Format und Bohrungen wählen, Motiv hochladen, bestellen. Lieferung nach Deutschland.',
      primaryCta: 'Schild konfigurieren',
      primaryHref: '/producten/reclamebord',
      support: 'Fragen zu Material, Datei oder Bestellung? Unser Kundenservice hilft Ihnen per E-Mail weiter.',
      usps: [
        ['Richtiges Material', 'Fassade, Baustelle, Ladenstraße oder Sportplatz: wir führen Sie zur Platte, nicht zum Jargon.'],
        ['Bohrungen und Stärke', 'Direkt in der Konfiguration, kein Extra-Mail-Pingpong.'],
        ['Firmenrechnung', 'MwSt. für DE über OSS. USt-IdNr. für B2B-Verlagerung.'],
        ['Nachdruck bei Fehler', 'Falscher Druck oder Transportbruch: Nachdruck in der Bestellung, nicht nur im Chat.'],
      ],
      productsTitle: 'Schilder nach Einsatz',
      products: [
        { slug: 'reclamebord', label: 'Werbeschild', text: 'Allround für Außenwerbung und Gelände.' },
        { slug: 'gevelbord', label: 'Fassadenschild', text: 'Eingang, Firma, Praxis.' },
        { slug: 'dibond', label: 'Dibond', text: 'Steif, flach, lange haltbar draußen.' },
        { slug: 'forex', label: 'Forex', text: 'Leicht, gut für Innen und kurz außen.' },
        { slug: 'a-borden', label: 'Kundenstopper', text: 'Gehweg und Gastronomie.' },
        { slug: 'vlak-makelaarsbord', label: 'Maklerschild', text: 'Zu verkaufen, zu vermieten, Projekt.' },
      ],
      faqTitle: 'Häufige Fragen zu Schildern',
      faqs: [
        { q: 'Dibond oder Forex?', a: 'Dibond für dauerhaft außen und große, plane Flächen. Forex wenn es leicht bleiben soll. Die Produktseite erklärt den Einsatz.' },
        { q: 'Liefern Sie nach Deutschland?', a: 'Ja. Versand und Lieferzeit stehen an der Kasse für DE.' },
        { q: 'Bohrungen möglich?', a: 'Ja, in der Konfiguration. Schreiben Sie die Position in die Bestellnotiz wenn nötig.' },
        { q: 'Geschäftskunden?', a: 'Ja, Rechnung auf Firmennamen. Mit gültiger USt-IdNr. Verlagerung der Steuerschuld.' },
        { q: 'Widerruf?', a: 'Personalisierte Schilder nach Ihrer Datei sind ausgenommen. Bei Fehler oder Bruch drucken wir neu.' },
        { q: 'Montage?', a: 'Lieferung unmontiert. Für Fassadenmontage können Sie uns um Rat fragen: support@printen.eu.' },
      ],
      relatedTitle: 'Weiter für Deutschland',
      related: [
        { id: 'stickers', label: 'Aufkleber' },
        { id: 'canvas', label: 'Foto auf Leinwand' },
      ],
    },
    nl: {
      pathKey: '/reclameborden',
      metaTitle: 'Reclameborden bestellen | Dibond, Forex, gevel | Printen.eu',
      metaDescription: 'Borden op maat: dibond, forex, gevel, makelaar, stoep. Bestandscheck inbegrepen, levering DE, NL, BE.',
      badge: 'Materiaal per toepassing',
      title: 'Reclameborden die buiten leesbaar blijven',
      subtitle: 'Dibond, forex of aluminium composiet. Formaat en boringen kiezen, ontwerp uploaden, bestellen.',
      primaryCta: 'Bord configureren',
      primaryHref: '/producten/reclamebord',
      support: 'Klantenservice: antwoord binnen 1 werkdag, kantooruren 09:00-17:00.',
      usps: [
        ['Juist materiaal', 'Gevel, bouw, winkelstraat of sportveld.'],
        ['Boringen en dikte', 'Direct in de configurator.'],
        ['Zakelijke factuur', 'OSS-btw in DE, VIES voor B2B.'],
        ['Herdruk bij fout', 'Verkeerde druk of transportbreuk: herdruk op de order.'],
      ],
      productsTitle: 'Borden per toepassing',
      products: [
        { slug: 'reclamebord', label: 'Reclamebord', text: 'Allround buitenreclame en terrein.' },
        { slug: 'gevelbord', label: 'Gevelbord', text: 'Entree, bedrijf, praktijk.' },
        { slug: 'dibond', label: 'Dibond', text: 'Stijf, vlak, langdurig buiten.' },
        { slug: 'forex', label: 'Forex', text: 'Licht, binnen en kort buiten.' },
        { slug: 'a-borden', label: 'Stoepborden', text: 'Winkelstraat en horeca.' },
        { slug: 'vlak-makelaarsbord', label: 'Makelaarsbord', text: 'Te koop, te huur, project.' },
      ],
      faqTitle: 'Veelgestelde vragen over borden',
      faqs: [
        { q: 'Dibond of forex?', a: 'Dibond voor duurzaam buiten en grote vlakke vlakken. Forex als het licht moet blijven.' },
        { q: 'Levering in Duitsland?', a: 'Ja. Verzending en levertijd in de kassa voor DE.' },
        { q: 'Boringen mogelijk?', a: 'Ja, in de configuratie. Zet de positie in de ordernotitie indien nodig.' },
        { q: 'Zakelijk?', a: 'Ja, factuur op bedrijfsnaam. Met geldig btw-nummer verlegging.' },
        { q: 'Herroeping?', a: 'Gepersonaliseerde borden vallen erbuiten. Bij fout of breuk drukken we opnieuw.' },
        { q: 'Montage?', a: 'Onbemont geleverd. Voor gevelmontage kun je ons mailen: support@printen.eu.' },
      ],
      relatedTitle: 'Ook bekeken',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'canvas', label: 'Foto op canvas' },
      ],
    },
    en: {
      pathKey: '/reclameborden',
      metaTitle: 'Custom sign boards | Dibond, Forex, facade | Printen.eu',
      metaDescription: 'Boards to size: dibond, forex, facade, estate agent, pavement. File check included. Delivery to Germany.',
      badge: 'Material by location',
      title: 'Sign boards that stay readable outdoors',
      subtitle: 'Dibond, forex or aluminium composite. Choose size and drill holes, upload artwork, order.',
      primaryCta: 'Configure a board',
      primaryHref: '/producten/reclamebord',
      support: 'Support: reply within 1 business day, office hours 09:00-17:00.',
      usps: [
        ['Right material', 'Facade, site, high street or sports ground.'],
        ['Holes and thickness', 'In the configurator, not by email tennis.'],
        ['Business invoice', 'OSS VAT in DE, reverse charge with a VAT number.'],
        ['Reprint on fault', 'Wrong print or transit break: reprint on the order.'],
      ],
      productsTitle: 'Boards by use',
      products: [
        { slug: 'reclamebord', label: 'Sign board', text: 'All-round outdoor and site use.' },
        { slug: 'gevelbord', label: 'Facade board', text: 'Entrance, company, practice.' },
        { slug: 'dibond', label: 'Dibond', text: 'Stiff, flat, durable outdoors.' },
        { slug: 'forex', label: 'Forex', text: 'Light, indoor and short outdoor.' },
        { slug: 'a-borden', label: 'Pavement signs', text: 'High street and hospitality.' },
        { slug: 'vlak-makelaarsbord', label: 'Estate agent board', text: 'For sale, to let, project.' },
      ],
      faqTitle: 'Sign board FAQ',
      faqs: [
        { q: 'Dibond or forex?', a: 'Dibond for lasting outdoor and large flat areas. Forex when it must stay light.' },
        { q: 'Delivery to Germany?', a: 'Yes. Shipping and lead time at checkout for DE.' },
        { q: 'Drill holes?', a: 'Yes, in the configuration. Put positions in the order note if needed.' },
        { q: 'Business orders?', a: 'Yes, invoice in the company name. Reverse charge with a valid VAT number.' },
        { q: 'Returns?', a: 'Personalised boards are excluded. Faults or breakage get a reprint.' },
        { q: 'Installation?', a: 'Shipped unmounted. Ask us about facade mounting: support@printen.eu.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'canvas', label: 'Photo on canvas' },
      ],
    },
    fr: {
      pathKey: '/reclameborden',
      metaTitle: 'Panneaux publicitaires | Dibond, Forex, facade | Printen.eu',
      metaDescription: 'Panneaux sur mesure. Contrôle de fichier inclus, livraison en Allemagne.',
      badge: 'Materiau selon l emplacement',
      title: 'Des panneaux lisibles dehors',
      subtitle: 'Dibond, forex ou composite alu. Format et percages, visuel, commande. Livraison en Allemagne.',
      primaryCta: 'Configurer un panneau',
      primaryHref: '/producten/reclamebord',
      support: 'Support: reponse sous 1 jour ouvre, horaires 09:00-17:00.',
      usps: [
        ['Bon materiau', 'Facade, chantier, rue ou stade.'],
        ['Percages et epaisseur', 'Dans le configurateur.'],
        ['Facture pro', 'TVA OSS en DE, autoliquidation avec n TVA.'],
        ['Reimpression si defaut', 'Erreur ou casse: reimpression sur la commande.'],
      ],
      productsTitle: 'Panneaux par usage',
      products: [
        { slug: 'reclamebord', label: 'Panneau pub', text: 'Exterieur et terrain.' },
        { slug: 'gevelbord', label: 'Enseigne facade', text: 'Entree, societe, cabinet.' },
        { slug: 'dibond', label: 'Dibond', text: 'Rigide, plan, durable dehors.' },
        { slug: 'forex', label: 'Forex', text: 'Leger, interieur et court exterieur.' },
        { slug: 'a-borden', label: 'Chevalet de trottoir', text: 'Rue et restauration.' },
        { slug: 'vlak-makelaarsbord', label: 'Panneau immo', text: 'A vendre, a louer, projet.' },
      ],
      faqTitle: 'Questions panneaux',
      faqs: [
        { q: 'Dibond ou forex?', a: 'Dibond pour un usage exterieur durable. Forex s il doit rester leger.' },
        { q: 'Livraison Allemagne?', a: 'Oui. Port et delai a la caisse pour DE.' },
        { q: 'Percages?', a: 'Oui, dans la configuration. Precisez la position en note.' },
        { q: 'Pro?', a: 'Oui, facture au nom de la societe. Autoliquidation avec n TVA valide.' },
        { q: 'Retour?', a: 'Le panneau personnalise est exclu. Defaut ou casse: reimpression.' },
        { q: 'Pose?', a: 'Livre non pose. Conseil facade: support@printen.eu.' },
      ],
      relatedTitle: 'A voir aussi',
      related: [
        { id: 'stickers', label: 'Stickers' },
        { id: 'canvas', label: 'Photo sur toile' },
      ],
    },
  },
  kerst: {
    nl: {
      pathKey: '/kerstkaarten',
      metaTitle: 'Kerstkaarten drukken met eigen ontwerp | Zakelijk en privé | Printen.eu',
      metaDescription: 'Kerstkaarten drukken met je eigen foto of ontwerp. Met envelop, foliedruk, op groeipapier of als pop-up kaart. Gratis bestandscontrole. Bestel online.',
      badge: 'Kerst 2026: bestel op tijd',
      title: 'Kerstkaarten drukken met je eigen ontwerp',
      subtitle: 'Voor familie, klanten of het hele team. Kies een kaart met envelop, een luxe kaart met foliedruk of een duurzame kaart die je kunt planten. Upload je ontwerp en bestel direct.',
      primaryCta: 'Kerstkaarten met envelop maken',
      primaryHref: '/producten/kaarten-met-envelop',
      support: 'Vragen over je bestand of een grote oplage? Mail ons via support@printen.eu.',
      usps: [
        ['Inclusief envelop', 'Kaarten met envelop in formaten van A6 tot A5, plat of gevouwen.'],
        ['Goud, zilver of koper', 'Foliedruk en luxe karton voor een kaart die echt opvalt.'],
        ['Op naam per ontvanger', 'Personaliseer vanuit Excel met een naam of tekst per kaart.'],
        ['Gratis bestandscontrole', 'Wij kijken je bestand na voordat het in productie gaat.'],
      ],
      productsTitle: 'Kerstkaarten, kalenders en meer voor de feestdagen',
      products: [
        { slug: 'kaarten-met-envelop', label: 'Kerstkaarten met envelop', text: 'De klassieker: plat of gevouwen, met bijpassende envelop.' },
        { slug: 'luxe-kaart', label: 'Luxe kerstkaarten', text: 'Zwaar karton met foliedruk, letterpress of gekleurde rand.' },
        { slug: 'ansichtkaarten', label: 'Kerstansichtkaarten', text: 'Voordelig in grote aantallen, met goud- of zilverfolie.' },
        { slug: 'groeipapier-kaarten', label: 'Groeipapier kerstkaart', text: 'Ook in kerstboomvorm. Na de feestdagen planten.' },
        { slug: 'pop-up-kaarten', label: 'Pop-up kerstkaart', text: 'Een kubus die openspringt, inclusief envelop.' },
        { slug: 'chocolade-kerstboom-met-sleeve', label: 'Chocolade kerstboom', text: 'Lekker extraatje bij de kaart, met eigen sleeve.' },
        { slug: 'adventskalender', label: 'Adventskalender met eigen ontwerp', text: 'Gevuld met chocolade en bedrukt met je eigen ontwerp of logo.' },
        { slug: 'wire-o-muurkalender', label: 'Kalender 2027', text: 'Full colour in A3 of A4 met ophanghaak: je eigen foto’s het hele jaar aan de muur.' },
        { slug: 'inhaker-kerstboom', label: 'Kerstboom van plaat', text: 'Een bedrukte kerstboom voor de etalage, de balie of het kantoor.' },
      ],
      faqTitle: 'Veelgestelde vragen over kerstkaarten',
      faqs: [
        { q: 'Wanneer moet ik mijn kerstkaarten bestellen?', a: 'Hoe eerder hoe beter: in december is het druk bij drukkerijen en post. De levertijd van jouw configuratie zie je in de configurator en bij het afrekenen.' },
        { q: 'Zit er een envelop bij?', a: 'Bij kaarten met envelop en pop-up kaarten zit een envelop inbegrepen. Bij ansichtkaarten en luxe kaarten niet.' },
        { q: 'Kan ik elke kaart een eigen naam geven?', a: 'Ja, bij kaarten met envelop, ansichtkaarten en groeipapier kaarten kun je personaliseren vanuit Excel, met een naam of tekst per kaart.' },
        { q: 'Kan ik kerstkaarten bestellen als bedrijf?', a: 'Ja. Je krijgt een factuur op bedrijfsnaam, en binnen de EU met een geldig btw-nummer wordt de btw verlegd.' },
        { q: 'Welk bestand moet ik aanleveren?', a: 'Een PDF op het gekozen formaat met 3 mm afloop werkt het best. Wij controleren je bestand gratis voordat het gedrukt wordt.' },
        { q: 'Wat als er iets mis is met de druk?', a: 'Mail een foto en je ordernummer naar support@printen.eu. Bij een drukfout of transportschade drukken we opnieuw of krijg je je geld terug.' },
      ],
      relatedTitle: 'Ook handig rond de feestdagen',
      related: [
        { id: 'canvas', label: 'Foto op canvas' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    de: {
      pathKey: '/kerstkaarten',
      metaTitle: 'Weihnachtskarten drucken mit eigenem Motiv | Firma und privat | Printen.eu',
      metaDescription: 'Weihnachtskarten mit eigenem Foto oder Design drucken. Mit Umschlag, Folienveredelung, auf Saatpapier oder als Pop-up-Karte. Dateiprüfung inklusive.',
      badge: 'Weihnachten 2026: rechtzeitig bestellen',
      title: 'Weihnachtskarten mit eigenem Motiv drucken',
      subtitle: 'Für Familie, Kunden oder das ganze Team. Wählen Sie eine Karte mit Umschlag, eine Luxuskarte mit Folienprägung oder eine nachhaltige Karte zum Einpflanzen.',
      primaryCta: 'Weihnachtskarten mit Umschlag gestalten',
      primaryHref: '/producten/kaarten-met-envelop',
      support: 'Fragen zur Datei oder zu großen Auflagen? Schreiben Sie uns per E-Mail an support@printen.eu.',
      usps: [
        ['Mit Umschlag', 'Karten mit Umschlag von A6 bis A5, flach oder gefalzt.'],
        ['Gold, Silber oder Kupfer', 'Folienveredelung und schwerer Karton für eine Karte, die auffällt.'],
        ['Mit Namen pro Empfänger', 'Personalisierung aus Excel mit Name oder Text pro Karte.'],
        ['Kostenlose Dateiprüfung', 'Wir prüfen Ihre Datei vor der Produktion.'],
      ],
      productsTitle: 'Weihnachtskarten, Kalender und mehr für die Festtage',
      products: [
        { slug: 'kaarten-met-envelop', label: 'Weihnachtskarten mit Umschlag', text: 'Der Klassiker: flach oder gefalzt, mit passendem Umschlag.' },
        { slug: 'luxe-kaart', label: 'Luxus-Weihnachtskarten', text: 'Schwerer Karton mit Folienprägung, Letterpress oder Farbschnitt.' },
        { slug: 'ansichtkaarten', label: 'Weihnachtspostkarten', text: 'Günstig in großen Mengen, mit Gold- oder Silberfolie.' },
        { slug: 'groeipapier-kaarten', label: 'Weihnachtskarte aus Saatpapier', text: 'Auch in Tannenbaumform. Nach den Feiertagen einpflanzen.' },
        { slug: 'pop-up-kaarten', label: 'Pop-up-Weihnachtskarte', text: 'Ein Würfel, der aufspringt, inklusive Umschlag.' },
        { slug: 'chocolade-kerstboom-met-sleeve', label: 'Schoko-Tannenbaum', text: 'Süße Beigabe zur Karte, mit eigener Banderole.' },
        { slug: 'adventskalender', label: 'Adventskalender mit eigenem Motiv', text: 'Mit Schokolade gefüllt und mit Ihrem Motiv oder Logo bedruckt.' },
        { slug: 'wire-o-muurkalender', label: 'Kalender 2027', text: 'Vollfarbig in A3 oder A4 mit Aufhänger: Ihre eigenen Fotos das ganze Jahr an der Wand.' },
        { slug: 'inhaker-kerstboom', label: 'Weihnachtsbaum aus Platte', text: 'Ein bedruckter Weihnachtsbaum für Schaufenster, Empfang oder Büro.' },
      ],
      faqTitle: 'Häufige Fragen zu Weihnachtskarten',
      faqs: [
        { q: 'Wann sollte ich Weihnachtskarten bestellen?', a: 'Je früher, desto besser: Im Dezember sind Druckereien und Post stark ausgelastet. Die Lieferzeit Ihrer Konfiguration sehen Sie im Konfigurator und an der Kasse.' },
        { q: 'Ist ein Umschlag dabei?', a: 'Bei Karten mit Umschlag und Pop-up-Karten ist ein Umschlag inklusive. Bei Postkarten und Luxuskarten nicht.' },
        { q: 'Kann jede Karte einen eigenen Namen bekommen?', a: 'Ja, bei Karten mit Umschlag, Postkarten und Saatpapierkarten ist eine Personalisierung aus Excel möglich, mit Name oder Text pro Karte.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Sie erhalten eine Rechnung auf den Firmennamen. Innerhalb der EU gilt mit gültiger USt-IdNr. die Umkehrung der Steuerschuld.' },
        { q: 'Welche Datei brauche ich?', a: 'Am besten ein PDF im gewählten Format mit 3 mm Beschnitt. Wir prüfen Ihre Datei kostenlos vor dem Druck.' },
        { q: 'Was, wenn der Druck fehlerhaft ist?', a: 'Mailen Sie ein Foto und die Bestellnummer an support@printen.eu. Bei Druckfehler oder Transportschaden gibt es Nachdruck oder Erstattung.' },
      ],
      relatedTitle: 'Ebenfalls passend zu den Feiertagen',
      related: [
        { id: 'canvas', label: 'Foto auf Leinwand' },
        { id: 'stickers', label: 'Aufkleber' },
      ],
    },
    en: {
      pathKey: '/kerstkaarten',
      metaTitle: 'Custom Christmas cards printed | Business and personal | Printen.eu',
      metaDescription: 'Print Christmas cards with your own photo or design. With envelopes, foil, on seed paper or as a pop-up card. Free file check. Order online.',
      badge: 'Christmas 2026: order in good time',
      title: 'Print Christmas cards with your own design',
      subtitle: 'For family, clients or the whole team. Choose a card with envelope, a luxury card with foil or a sustainable card you can plant. Upload your design and order right away.',
      primaryCta: 'Create Christmas cards with envelopes',
      primaryHref: '/producten/kaarten-met-envelop',
      support: 'Questions about your file or a large run? Email us at support@printen.eu.',
      usps: [
        ['Envelopes included', 'Cards with envelopes from A6 to A5, flat or folded.'],
        ['Gold, silver or copper', 'Foil finishing and heavy board for a card that stands out.'],
        ['A name per recipient', 'Personalise from Excel with a name or text on each card.'],
        ['Free file check', 'We check your file before it goes into production.'],
      ],
      productsTitle: 'Christmas cards, calendars and more for the holidays',
      products: [
        { slug: 'kaarten-met-envelop', label: 'Christmas cards with envelopes', text: 'The classic: flat or folded, with a matching envelope.' },
        { slug: 'luxe-kaart', label: 'Luxury Christmas cards', text: 'Heavy board with foil, letterpress or coloured edges.' },
        { slug: 'ansichtkaarten', label: 'Christmas postcards', text: 'Great value in large runs, with gold or silver foil.' },
        { slug: 'groeipapier-kaarten', label: 'Seed paper Christmas card', text: 'Also in a Christmas tree shape. Plant it after the holidays.' },
        { slug: 'pop-up-kaarten', label: 'Pop-up Christmas card', text: 'A cube that springs open, envelope included.' },
        { slug: 'chocolade-kerstboom-met-sleeve', label: 'Chocolate Christmas tree', text: 'A sweet extra with the card, in your own sleeve.' },
        { slug: 'adventskalender', label: 'Custom advent calendar', text: 'Filled with chocolate and printed with your own design or logo.' },
        { slug: 'wire-o-muurkalender', label: '2027 calendar', text: 'Full colour in A3 or A4 with a hanger: your own photos on the wall all year.' },
        { slug: 'inhaker-kerstboom', label: 'Printed Christmas tree board', text: 'A printed Christmas tree for the shop window, reception or office.' },
      ],
      faqTitle: 'Christmas card FAQ',
      faqs: [
        { q: 'When should I order my Christmas cards?', a: 'The sooner the better: printers and postal services are busy in December. You can see the lead time for your configuration in the configurator and at checkout.' },
        { q: 'Are envelopes included?', a: 'Cards with envelopes and pop-up cards come with an envelope. Postcards and luxury cards do not.' },
        { q: 'Can each card have its own name?', a: 'Yes. Cards with envelopes, postcards and seed paper cards can be personalised from Excel, with a name or text per card.' },
        { q: 'Can I order as a business?', a: 'Yes. You get an invoice in your company name, and within the EU the VAT is reverse charged with a valid VAT number.' },
        { q: 'Which file should I supply?', a: 'A PDF at your chosen size with 3 mm bleed works best. We check your file for free before printing.' },
        { q: 'What if something is wrong with the print?', a: 'Email a photo and your order number to support@printen.eu. For a print fault or transit damage we reprint or refund.' },
      ],
      relatedTitle: 'Also handy for the holidays',
      related: [
        { id: 'canvas', label: 'Photo on canvas' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    fr: {
      pathKey: '/kerstkaarten',
      metaTitle: 'Cartes de Noël personnalisées à imprimer | Pro et particuliers | Printen.eu',
      metaDescription: 'Imprimez vos cartes de Noël avec votre photo ou votre visuel. Avec enveloppe, dorure, sur papier ensemencé ou en carte pop-up. Contrôle de fichier offert.',
      badge: 'Noël 2026 : commandez à temps',
      title: 'Imprimez vos cartes de Noël personnalisées',
      subtitle: 'Pour la famille, les clients ou toute l’équipe. Choisissez une carte avec enveloppe, une carte de luxe avec dorure ou une carte durable à planter.',
      primaryCta: 'Créer des cartes de Noël avec enveloppe',
      primaryHref: '/producten/kaarten-met-envelop',
      support: 'Une question sur votre fichier ou un gros tirage ? Écrivez-nous à support@printen.eu.',
      usps: [
        ['Enveloppe incluse', 'Cartes avec enveloppe de l’A6 à l’A5, à plat ou pliées.'],
        ['Or, argent ou cuivre', 'Dorure à chaud et carton épais pour une carte qui se remarque.'],
        ['Un nom par destinataire', 'Personnalisation depuis Excel avec un nom ou un texte par carte.'],
        ['Contrôle de fichier offert', 'Nous vérifions votre fichier avant la production.'],
      ],
      productsTitle: 'Cartes de Noël, calendriers et plus pour les fêtes',
      products: [
        { slug: 'kaarten-met-envelop', label: 'Cartes de Noël avec enveloppe', text: 'Le classique : à plat ou pliée, avec enveloppe assortie.' },
        { slug: 'luxe-kaart', label: 'Cartes de Noël de luxe', text: 'Carton épais avec dorure, letterpress ou tranche colorée.' },
        { slug: 'ansichtkaarten', label: 'Cartes postales de Noël', text: 'Avantageuses en grande quantité, avec dorure or ou argent.' },
        { slug: 'groeipapier-kaarten', label: 'Carte de Noël en papier ensemencé', text: 'Aussi en forme de sapin. À planter après les fêtes.' },
        { slug: 'pop-up-kaarten', label: 'Carte de Noël pop-up', text: 'Un cube qui se déploie, enveloppe incluse.' },
        { slug: 'chocolade-kerstboom-met-sleeve', label: 'Sapin en chocolat', text: 'Une douceur avec la carte, dans votre propre fourreau.' },
        { slug: 'adventskalender', label: 'Calendrier de l’Avent personnalisé', text: 'Garni de chocolat et imprimé avec votre visuel ou votre logo.' },
        { slug: 'wire-o-muurkalender', label: 'Calendrier 2027', text: 'En couleur, A3 ou A4 avec crochet : vos propres photos au mur toute l’année.' },
        { slug: 'inhaker-kerstboom', label: 'Sapin de Noël imprimé', text: 'Un sapin imprimé pour la vitrine, l’accueil ou le bureau.' },
      ],
      faqTitle: 'Questions sur les cartes de Noël',
      faqs: [
        { q: 'Quand commander mes cartes de Noël ?', a: 'Le plus tôt possible : en décembre, imprimeurs et services postaux sont très sollicités. Le délai de votre configuration s’affiche dans le configurateur et au paiement.' },
        { q: 'L’enveloppe est-elle fournie ?', a: 'Oui pour les cartes avec enveloppe et les cartes pop-up. Non pour les cartes postales et les cartes de luxe.' },
        { q: 'Chaque carte peut-elle porter un nom ?', a: 'Oui. Les cartes avec enveloppe, les cartes postales et les cartes en papier ensemencé se personnalisent depuis Excel, avec un nom ou un texte par carte.' },
        { q: 'Puis-je commander en tant qu’entreprise ?', a: 'Oui. Vous recevez une facture au nom de la société et, dans l’UE, la TVA est autoliquidée avec un numéro de TVA valide.' },
        { q: 'Quel fichier fournir ?', a: 'Un PDF au format choisi avec 3 mm de fond perdu est idéal. Nous vérifions votre fichier gratuitement avant l’impression.' },
        { q: 'Et si l’impression est défectueuse ?', a: 'Envoyez une photo et votre numéro de commande à support@printen.eu. En cas de défaut d’impression ou de dommage au transport, nous réimprimons ou remboursons.' },
      ],
      relatedTitle: 'Pratique aussi pour les fêtes',
      related: [
        { id: 'canvas', label: 'Photo sur toile' },
        { id: 'stickers', label: 'Autocollants' },
      ],
    },
  },
  bouw: {
    nl: {
      pathKey: '/bouwborden',
      metaTitle: 'Bouwbord laten maken | Werfbord, bouwhekdoek, steigerdoek | Printen.eu',
      metaDescription: 'Bouwbord of werfbord op maat met je eigen ontwerp. Kies dibond, kanaalplaat of forex, met boorgaten. Ook bouwhekdoeken en steigerdoek. Bestel online.',
      badge: 'Voor aannemers, ontwikkelaars en makelaars',
      title: 'Bouwborden en bouwplaatsreclame op maat',
      subtitle: 'Laat zien wie er bouwt, wat er komt en wie je kunt bellen. Stel je bouwbord samen in je eigen formaat en materiaal, en maak de bouwplaats compleet met bouwhekdoeken en steigerdoek.',
      primaryCta: 'Bouwbord samenstellen',
      primaryHref: '/producten/reclamebord',
      support: 'Een groot project of meerdere bouwplaatsen? Mail ons via support@printen.eu.',
      usps: [
        ['Eigen formaat', 'Breedte en hoogte vul je zelf in, van klein projectbord tot groot werfbord.'],
        ['Materiaal per looptijd', 'Kanaalplaat voor een korte klus, dibond voor een project van jaren.'],
        ['Boorgaten erin', 'Kies gaten boven, in elke hoek of op de plek uit je bestand.'],
        ['Gratis bestandscontrole', 'Wij kijken je bestand na voordat het in productie gaat.'],
      ],
      productsTitle: 'Alles voor de bouwplaats',
      products: [
        { slug: 'reclamebord', label: 'Bouwbord op maat', text: 'Uit 22 plaatmaterialen, enkel- of dubbelzijdig, met boorgaten.' },
        { slug: 'dibond', label: 'Dibond bouwbord', text: 'Stijf aluminium sandwichpaneel voor jarenlang buiten.' },
        { slug: 'bouwhekdoeken', label: 'Bouwhekdoek', text: '338 x 174 cm, mesh of frontlit, met ringen om de 30 cm.' },
        { slug: 'hekwerkbanners', label: 'Hekwerkbanner', text: 'Voor bouwhekken en dranghekken in vaste maten.' },
        { slug: 'mesh-banner', label: 'Steigerdoek (mesh)', text: 'Laat wind door, in je eigen formaat met zomen en ringen.' },
        { slug: 'vrijstaand-spandoekframe', label: 'Vrijstaand frame', text: 'Aluminium frame met voeten om een doek of bord neer te zetten.' },
      ],
      faqTitle: 'Veelgestelde vragen over bouwborden',
      faqs: [
        { q: 'Welk materiaal kies ik voor een bouwbord?', a: 'Voor een bouwbord dat jaren buiten staat is dibond de beste keuze: stijf, vlak en weerbestendig. Kanaalplaat is licht en voordelig voor een kortere periode. Forex past bij binnen of kort buiten.' },
        { q: 'Wat is het verschil tussen een bouwbord en een werfbord?', a: 'Geen: in Nederland heet het een bouwbord, in Vlaanderen een werfbord. Het gaat om hetzelfde informatiebord bij een bouwproject.' },
        { q: 'Kunnen er gaten in voor bevestiging?', a: 'Ja. Kies twee gaten boven, een gat in elke hoek of zet de gaten in je drukbestand. Diameter en afstand tot de rand stel je zelf in.' },
        { q: 'Kan het bord tegen graffiti?', a: 'Ja, bij het reclamebord kun je een anti-graffiti beschermlaag kiezen, naast een matte of glanzende laag.' },
        { q: 'Kan ik als bedrijf bestellen?', a: 'Ja. Je krijgt een factuur op bedrijfsnaam, en binnen de EU met een geldig btw-nummer wordt de btw verlegd.' },
        { q: 'Wat als er iets mis is met de druk?', a: 'Mail een foto en je ordernummer naar support@printen.eu. Bij een drukfout of transportschade drukken we opnieuw of krijg je je geld terug.' },
      ],
      relatedTitle: 'Ook bekeken',
      related: [
        { id: 'borden', label: 'Reclameborden' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    de: {
      pathKey: '/bouwborden',
      metaTitle: 'Bauschild drucken lassen | Baustellenschild, Bauzaunbanner | Printen.eu',
      metaDescription: 'Bauschild nach Maß mit eigenem Design. Dibond, Hohlkammerplatte oder Forex, mit Bohrungen. Auch Bauzaunbanner und Gerüstplanen. Online bestellen.',
      badge: 'Für Bauunternehmen, Projektentwickler und Makler',
      title: 'Bauschilder und Baustellenwerbung nach Maß',
      subtitle: 'Zeigen Sie, wer baut, was entsteht und wen man anrufen kann. Stellen Sie Ihr Bauschild in eigenem Format und Material zusammen, ergänzt durch Bauzaunbanner und Gerüstplanen.',
      primaryCta: 'Bauschild gestalten',
      primaryHref: '/producten/reclamebord',
      support: 'Großes Projekt oder mehrere Baustellen? Schreiben Sie uns per E-Mail an support@printen.eu.',
      usps: [
        ['Eigenes Format', 'Breite und Höhe geben Sie selbst ein, vom kleinen Projektschild bis zum großen Bauschild.'],
        ['Material nach Laufzeit', 'Hohlkammerplatte für kurze Projekte, Dibond für Jahre.'],
        ['Mit Bohrungen', 'Löcher oben, in jeder Ecke oder an der Stelle aus Ihrer Datei.'],
        ['Kostenlose Dateiprüfung', 'Wir prüfen Ihre Datei vor der Produktion.'],
      ],
      productsTitle: 'Alles für die Baustelle',
      products: [
        { slug: 'reclamebord', label: 'Bauschild nach Maß', text: 'Aus 22 Plattenmaterialien, ein- oder beidseitig, mit Bohrungen.' },
        { slug: 'dibond', label: 'Bauschild aus Dibond', text: 'Steife Aluverbundplatte für jahrelangen Außeneinsatz.' },
        { slug: 'bouwhekdoeken', label: 'Bauzaunbanner', text: '338 x 174 cm, Mesh oder Frontlit, mit Ösen alle 30 cm.' },
        { slug: 'hekwerkbanners', label: 'Zaunbanner', text: 'Für Bauzäune und Absperrgitter in festen Maßen.' },
        { slug: 'mesh-banner', label: 'Gerüstplane (Mesh)', text: 'Winddurchlässig, im eigenen Format mit Saum und Ösen.' },
        { slug: 'vrijstaand-spandoekframe', label: 'Freistehender Rahmen', text: 'Aluminiumrahmen mit Füßen zum Aufstellen von Banner oder Schild.' },
      ],
      faqTitle: 'Häufige Fragen zu Bauschildern',
      faqs: [
        { q: 'Welches Material passt für ein Bauschild?', a: 'Für ein Bauschild, das jahrelang draußen steht, ist Dibond die beste Wahl: steif, eben und wetterfest. Hohlkammerplatte ist leicht und günstig für kürzere Zeit. Forex passt für innen oder kurz außen.' },
        { q: 'Sind Bohrungen zur Befestigung möglich?', a: 'Ja. Wählen Sie zwei Löcher oben, ein Loch in jeder Ecke oder legen Sie die Löcher in Ihrer Druckdatei fest. Durchmesser und Randabstand stellen Sie selbst ein.' },
        { q: 'Gibt es einen Schutz gegen Graffiti?', a: 'Ja, beim Werbeschild können Sie eine Anti-Graffiti-Schutzschicht wählen, neben matt oder glänzend.' },
        { q: 'Was ist der Unterschied zwischen Bauzaunbanner und Gerüstplane?', a: 'Das Bauzaunbanner hat das feste Maß eines Bauzaunelements. Die Gerüstplane aus Mesh bestellen Sie im eigenen Format für Gerüst oder Fassade.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Sie erhalten eine Rechnung auf den Firmennamen. Innerhalb der EU gilt mit gültiger USt-IdNr. die Umkehrung der Steuerschuld.' },
        { q: 'Was, wenn der Druck fehlerhaft ist?', a: 'Mailen Sie ein Foto und die Bestellnummer an support@printen.eu. Bei Druckfehler oder Transportschaden gibt es Nachdruck oder Erstattung.' },
      ],
      relatedTitle: 'Ebenfalls angesehen',
      related: [
        { id: 'borden', label: 'Werbeschilder' },
        { id: 'stickers', label: 'Aufkleber' },
      ],
    },
    en: {
      pathKey: '/bouwborden',
      metaTitle: 'Construction site signs made to size | Hoarding, fence banners | Printen.eu',
      metaDescription: 'Construction site sign to size with your own design. Dibond, corrugated board or Forex, with drill holes. Also fence banners and scaffold mesh. Order online.',
      badge: 'For contractors, developers and estate agents',
      title: 'Construction site signs and site advertising',
      subtitle: 'Show who is building, what is coming and who to call. Build your site sign in your own size and material, and complete the site with fence banners and scaffold mesh.',
      primaryCta: 'Build a site sign',
      primaryHref: '/producten/reclamebord',
      support: 'A large project or several sites? Email us at support@printen.eu.',
      usps: [
        ['Your own size', 'Enter width and height yourself, from a small project sign to a large site board.'],
        ['Material by duration', 'Corrugated board for a short job, Dibond for a project that runs for years.'],
        ['Drill holes included', 'Holes at the top, in each corner or where your file shows them.'],
        ['Free file check', 'We check your file before it goes into production.'],
      ],
      productsTitle: 'Everything for the site',
      products: [
        { slug: 'reclamebord', label: 'Site sign to size', text: 'From 22 board materials, single or double sided, with drill holes.' },
        { slug: 'dibond', label: 'Dibond site sign', text: 'Stiff aluminium composite for years outdoors.' },
        { slug: 'bouwhekdoeken', label: 'Fence banner', text: '338 x 174 cm, mesh or frontlit, with eyelets every 30 cm.' },
        { slug: 'hekwerkbanners', label: 'Barrier banner', text: 'For site fences and crowd barriers in fixed sizes.' },
        { slug: 'mesh-banner', label: 'Scaffold mesh', text: 'Lets wind through, in your own size with hems and eyelets.' },
        { slug: 'vrijstaand-spandoekframe', label: 'Freestanding frame', text: 'Aluminium frame with feet to stand up a banner or sign.' },
      ],
      faqTitle: 'Construction site sign FAQ',
      faqs: [
        { q: 'Which material should I choose?', a: 'For a sign that stays outdoors for years, Dibond is the best choice: stiff, flat and weatherproof. Corrugated board is light and affordable for a shorter period. Forex suits indoor or short outdoor use.' },
        { q: 'Can you drill holes for mounting?', a: 'Yes. Choose two holes at the top, one in each corner or mark the holes in your print file. You set the diameter and edge distance yourself.' },
        { q: 'Is there protection against graffiti?', a: 'Yes, the sign board offers an anti-graffiti protective layer, besides matt or gloss.' },
        { q: 'What is the difference between a fence banner and scaffold mesh?', a: 'A fence banner has the fixed size of a site fence panel. Scaffold mesh is ordered in your own size for scaffolding or facades.' },
        { q: 'Can I order as a business?', a: 'Yes. You get an invoice in your company name, and within the EU the VAT is reverse charged with a valid VAT number.' },
        { q: 'What if something is wrong with the print?', a: 'Email a photo and your order number to support@printen.eu. For a print fault or transit damage we reprint or refund.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'borden', label: 'Sign boards' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    fr: {
      pathKey: '/bouwborden',
      metaTitle: 'Panneau de chantier sur mesure | Bâche de clôture, filet | Printen.eu',
      metaDescription: 'Panneau de chantier sur mesure à votre image. Dibond, plaque alvéolaire ou Forex, avec perçages. Aussi bâches de clôture et filets d’échafaudage.',
      badge: 'Pour entreprises du bâtiment, promoteurs et agents immobiliers',
      title: 'Panneaux de chantier et publicité de chantier',
      subtitle: 'Montrez qui construit, ce qui arrive et qui contacter. Composez votre panneau de chantier au format et dans le matériau de votre choix, et complétez avec des bâches de clôture et des filets.',
      primaryCta: 'Composer un panneau de chantier',
      primaryHref: '/producten/reclamebord',
      support: 'Un grand projet ou plusieurs chantiers ? Écrivez-nous à support@printen.eu.',
      usps: [
        ['Votre format', 'Largeur et hauteur au choix, du petit panneau au grand panneau de chantier.'],
        ['Matériau selon la durée', 'Plaque alvéolaire pour un chantier court, Dibond pour plusieurs années.'],
        ['Perçages inclus', 'Trous en haut, dans chaque coin ou selon votre fichier.'],
        ['Contrôle de fichier offert', 'Nous vérifions votre fichier avant la production.'],
      ],
      productsTitle: 'Tout pour le chantier',
      products: [
        { slug: 'reclamebord', label: 'Panneau de chantier sur mesure', text: '22 matériaux, recto ou recto verso, avec perçages.' },
        { slug: 'dibond', label: 'Panneau de chantier Dibond', text: 'Composite aluminium rigide pour des années dehors.' },
        { slug: 'bouwhekdoeken', label: 'Bâche de clôture', text: '338 x 174 cm, mesh ou frontlit, œillets tous les 30 cm.' },
        { slug: 'hekwerkbanners', label: 'Bâche pour barrière', text: 'Pour clôtures de chantier et barrières en formats fixes.' },
        { slug: 'mesh-banner', label: 'Filet d’échafaudage (mesh)', text: 'Laisse passer le vent, à votre format avec ourlets et œillets.' },
        { slug: 'vrijstaand-spandoekframe', label: 'Cadre autoportant', text: 'Cadre aluminium avec pieds pour installer une bâche ou un panneau.' },
      ],
      faqTitle: 'Questions sur les panneaux de chantier',
      faqs: [
        { q: 'Quel matériau choisir ?', a: 'Pour un panneau qui reste dehors pendant des années, le Dibond est le meilleur choix : rigide, plan et résistant aux intempéries. La plaque alvéolaire est légère et économique pour une durée plus courte. Le Forex convient à l’intérieur ou à un usage extérieur court.' },
        { q: 'Peut-on percer des trous de fixation ?', a: 'Oui. Choisissez deux trous en haut, un trou dans chaque coin ou indiquez les trous dans votre fichier. Vous réglez le diamètre et la distance au bord.' },
        { q: 'Existe-t-il une protection anti-graffiti ?', a: 'Oui, le panneau publicitaire propose une couche de protection anti-graffiti, en plus du mat ou du brillant.' },
        { q: 'Quelle différence entre bâche de clôture et filet d’échafaudage ?', a: 'La bâche de clôture a le format fixe d’un panneau de clôture de chantier. Le filet mesh se commande à votre format pour un échafaudage ou une façade.' },
        { q: 'Puis-je commander en tant qu’entreprise ?', a: 'Oui. Vous recevez une facture au nom de la société et, dans l’UE, la TVA est autoliquidée avec un numéro de TVA valide.' },
        { q: 'Et si l’impression est défectueuse ?', a: 'Envoyez une photo et votre numéro de commande à support@printen.eu. En cas de défaut d’impression ou de dommage au transport, nous réimprimons ou remboursons.' },
      ],
      relatedTitle: 'À voir aussi',
      related: [
        { id: 'borden', label: 'Panneaux publicitaires' },
        { id: 'stickers', label: 'Autocollants' },
      ],
    },
  },
  inhaker: {
    nl: {
      pathKey: '/inhaker-systeem',
      metaTitle: 'Inhaker systeem: palen, voetplaten en bedrukte panelen | Printen.eu',
      metaDescription: 'Bouw met het Inhaker systeem je eigen display, bord, wegwijzer of beursstand. Zwarte palen, voetplaten en bedrukte panelen die je erin haakt. Bestel online.',
      badge: 'Display, bord of beursstand uit één systeem',
      title: 'Het Inhaker systeem: palen en panelen die je in elkaar haakt',
      subtitle: 'Een Inhaker is een zwarte paal waar je bedrukte panelen tot 10 mm dik in haakt. Met een paar palen, voetplaten en panelen bouw je een display, reclamebord, wegwijzer, vrijstaande wand of complete beursstand, en je bouwt het net zo makkelijk weer om.',
      primaryCta: 'Bekijk de Inhaker paal',
      primaryHref: '/producten/inhaker-staander',
      support: 'Twijfel je welke onderdelen je nodig hebt? Mail je plan of schets naar support@printen.eu en we rekenen mee.',
      usps: [
        ['Haken, niet schroeven', 'Panelen met uitgefreesde haken hang je zo in de paal, zonder gereedschap.'],
        ['Zeven hoogtes', 'Palen van 45 cm voor op de balie tot 300 cm voor een wand of beursstand.'],
        ['Steeds opnieuw te gebruiken', 'Wissel alleen de panelen voor een nieuwe actie of beurs; de palen blijven.'],
        ['Gerecycled materiaal', 'De palen zijn gemaakt van hoogwaardig gerecycled pvc.'],
      ],
      productsTitle: 'De onderdelen van het systeem',
      products: [
        { slug: 'inhaker-staander', label: 'Inhaker paal', text: 'De basis: zwarte paal in zeven hoogtes, inclusief koppelstuk.' },
        { slug: 'inhaker-voetplaat', label: 'Voetplaat', text: 'Stalen voet om de paal vrij in de ruimte neer te zetten.' },
        { slug: 'inhaker-plaatmateriaal', label: 'Bedrukte panelen', text: 'Re-board of Forex van 10 mm met uitgefreesde haken, in vaste vormen.' },
        { slug: 'inhaker-beursstand', label: 'Complete beursstand', text: 'Bedrukte wand van 88 cm breed in drie hoogtes, klaar om op te bouwen.' },
        { slug: 'inhaker-kerstboom', label: 'Kerstboom', text: 'Bedrukte kerstboom van 127,5 x 168 cm voor etalage of kantoor.' },
        { slug: 'inhaker-wandhaak', label: 'Wandhaak met lock', text: 'Hang een paal aan de muur in plaats van hem neer te zetten.' },
        { slug: 'inhaker-koppelstuk', label: 'Koppelstuk', text: 'Zet een paal vast op vloer, plafond, muur of een andere paal.' },
        { slug: 'inhaker-haken-met-plakstrip', label: 'Haken met plakstrip', text: 'Voor een paneel zonder gefreesde haken: plakken en ophangen.' },
        { slug: 'inhaker-inslagdop', label: 'Inslagdop', text: 'Werkt de zichtbare bovenkant van een korte paal netjes af.' },
      ],
      faqTitle: 'Veelgestelde vragen over het Inhaker systeem',
      faqs: [
        { q: 'Wat heb ik minimaal nodig?', a: 'Voor een vrijstaand bord: twee palen, twee voetplaten en een bedrukt paneel dat tussen de palen hangt. Bij elke paal zit al een koppelstuk om hem op de voetplaat te zetten.' },
        { q: 'Hoe dik mogen de panelen zijn?', a: 'Je haakt panelen tot 10 mm dik in de paal. Onze bedrukte Inhaker panelen zijn van 10 mm Re-board of Forex en hebben de haken al uitgefreesd.' },
        { q: 'Mijn paneel heeft geen haken. Kan het toch?', a: 'Ja, met de haken met plakstrip. Die plak je haaks op de achterkant van het paneel, zodat het in de paal past.' },
        { q: 'Kan ik de Inhaker buiten gebruiken?', a: 'De palen zijn waterafstotend maar bedoeld voor binnen. Voor buiten raden we een reclamebord of gevelbord aan.' },
        { q: 'Kan ik een paal aan de muur bevestigen?', a: 'Ja. Met de wandhaak met lock hang je de paal aan de muur, en met het koppelstuk zet je hem vast op vloer of plafond.' },
        { q: 'Kan ik als bedrijf bestellen?', a: 'Ja. Je krijgt een factuur op bedrijfsnaam, en binnen de EU met een geldig btw-nummer wordt de btw verlegd.' },
      ],
      relatedTitle: 'Ook handig',
      related: [
        { id: 'borden', label: 'Reclameborden' },
        { id: 'kerst', label: 'Kerst' },
      ],
    },
    de: {
      pathKey: '/inhaker-systeem',
      metaTitle: 'Inhaker System: Pfosten, Fußplatten und bedruckte Platten | Printen.eu',
      metaDescription: 'Bauen Sie mit dem Inhaker System Ihr eigenes Display, Schild, Wegweiser oder Messestand. Schwarze Pfosten, Fußplatten und bedruckte Platten zum Einhängen.',
      badge: 'Display, Schild oder Messestand aus einem System',
      title: 'Das Inhaker System: Pfosten und Platten zum Einhängen',
      subtitle: 'Ein Inhaker ist ein schwarzer Pfosten, in den Sie bedruckte Platten bis 10 mm Stärke einhängen. Aus wenigen Pfosten, Fußplatten und Platten bauen Sie ein Display, Werbeschild, Wegweiser, eine freistehende Wand oder einen kompletten Messestand, und genauso leicht wieder um.',
      primaryCta: 'Inhaker Pfosten ansehen',
      primaryHref: '/producten/inhaker-staander',
      support: 'Unsicher, welche Teile Sie brauchen? Schicken Sie Ihren Plan oder eine Skizze per E-Mail an support@printen.eu, wir rechnen mit.',
      usps: [
        ['Einhängen statt schrauben', 'Platten mit ausgefrästen Haken hängen Sie ohne Werkzeug in den Pfosten.'],
        ['Sieben Höhen', 'Pfosten von 45 cm für die Theke bis 300 cm für eine Wand oder einen Messestand.'],
        ['Immer wieder verwendbar', 'Für eine neue Aktion oder Messe tauschen Sie nur die Platten; die Pfosten bleiben.'],
        ['Recyceltes Material', 'Die Pfosten bestehen aus hochwertigem recyceltem PVC.'],
      ],
      productsTitle: 'Die Teile des Systems',
      products: [
        { slug: 'inhaker-staander', label: 'Inhaker Pfosten', text: 'Die Basis: schwarzer Pfosten in sieben Höhen, mit Kupplungsstück.' },
        { slug: 'inhaker-voetplaat', label: 'Fußplatte', text: 'Stahlfuß, um den Pfosten frei im Raum aufzustellen.' },
        { slug: 'inhaker-plaatmateriaal', label: 'Bedruckte Platten', text: 'Re-board oder Forex 10 mm mit ausgefrästen Haken, in festen Formen.' },
        { slug: 'inhaker-beursstand', label: 'Kompletter Messestand', text: 'Bedruckte Wand, 88 cm breit, in drei Höhen, bereit zum Aufbauen.' },
        { slug: 'inhaker-kerstboom', label: 'Weihnachtsbaum', text: 'Bedruckter Weihnachtsbaum, 127,5 x 168 cm, für Schaufenster oder Büro.' },
        { slug: 'inhaker-wandhaak', label: 'Wandhaken mit Lock', text: 'Hängen Sie einen Pfosten an die Wand, statt ihn aufzustellen.' },
        { slug: 'inhaker-koppelstuk', label: 'Kupplungsstück', text: 'Befestigt einen Pfosten an Boden, Decke, Wand oder einem anderen Pfosten.' },
        { slug: 'inhaker-haken-met-plakstrip', label: 'Haken mit Klebestreifen', text: 'Für eine Platte ohne gefräste Haken: aufkleben und einhängen.' },
        { slug: 'inhaker-inslagdop', label: 'Endkappe', text: 'Schließt die sichtbare Oberseite eines kurzen Pfostens sauber ab.' },
      ],
      faqTitle: 'Häufige Fragen zum Inhaker System',
      faqs: [
        { q: 'Was brauche ich mindestens?', a: 'Für ein freistehendes Schild: zwei Pfosten, zwei Fußplatten und eine bedruckte Platte zwischen den Pfosten. Jedem Pfosten liegt bereits ein Kupplungsstück für die Fußplatte bei.' },
        { q: 'Wie dick dürfen die Platten sein?', a: 'Sie hängen Platten bis 10 mm Stärke ein. Unsere bedruckten Inhaker Platten bestehen aus 10 mm Re-board oder Forex mit bereits ausgefrästen Haken.' },
        { q: 'Meine Platte hat keine Haken. Geht es trotzdem?', a: 'Ja, mit den Haken mit Klebestreifen. Sie kleben sie rechtwinklig auf die Rückseite der Platte, damit sie in den Pfosten passt.' },
        { q: 'Kann ich den Inhaker draußen verwenden?', a: 'Die Pfosten sind wasserabweisend, aber für innen gedacht. Für draußen empfehlen wir ein Werbeschild oder Fassadenschild.' },
        { q: 'Kann ich einen Pfosten an der Wand befestigen?', a: 'Ja. Mit dem Wandhaken mit Lock hängen Sie den Pfosten an die Wand, mit dem Kupplungsstück befestigen Sie ihn an Boden oder Decke.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Sie erhalten eine Rechnung auf Ihren Firmennamen, und innerhalb der EU wird die Mehrwertsteuer mit gültiger USt-IdNr. umgekehrt.' },
      ],
      relatedTitle: 'Auch interessant',
      related: [
        { id: 'borden', label: 'Werbeschilder' },
        { id: 'kerst', label: 'Weihnachten' },
      ],
    },
    en: {
      pathKey: '/inhaker-systeem',
      metaTitle: 'Inhaker system: posts, base plates and printed panels | Printen.eu',
      metaDescription: 'Build your own display, sign, wayfinding or trade show stand with the Inhaker system. Black posts, base plates and printed panels that hook in. Order online.',
      badge: 'Display, sign or trade show stand from one system',
      title: 'The Inhaker system: posts and panels that hook together',
      subtitle: 'An Inhaker is a black post that holds printed panels up to 10 mm thick. With a few posts, base plates and panels you build a display, sign, wayfinding, a freestanding wall or a complete trade show stand, and rebuild it just as easily.',
      primaryCta: 'See the Inhaker post',
      primaryHref: '/producten/inhaker-staander',
      support: 'Not sure which parts you need? Email your plan or sketch to support@printen.eu and we will help you work it out.',
      usps: [
        ['Hook, don’t screw', 'Panels with milled hooks hang straight into the post, no tools needed.'],
        ['Seven heights', 'Posts from 45 cm for a counter to 300 cm for a wall or trade show stand.'],
        ['Reusable', 'For a new campaign or show you only swap the panels; the posts stay.'],
        ['Recycled material', 'The posts are made of high quality recycled PVC.'],
      ],
      productsTitle: 'The parts of the system',
      products: [
        { slug: 'inhaker-staander', label: 'Inhaker post', text: 'The base: a black post in seven heights, connector included.' },
        { slug: 'inhaker-voetplaat', label: 'Base plate', text: 'Steel base to stand the post freely in a room.' },
        { slug: 'inhaker-plaatmateriaal', label: 'Printed panels', text: '10 mm Re-board or Forex with milled hooks, in fixed shapes.' },
        { slug: 'inhaker-beursstand', label: 'Complete trade show stand', text: 'Printed wall, 88 cm wide in three heights, ready to build.' },
        { slug: 'inhaker-kerstboom', label: 'Christmas tree', text: 'Printed Christmas tree, 127.5 x 168 cm, for shop window or office.' },
        { slug: 'inhaker-wandhaak', label: 'Wall hook with lock', text: 'Hang a post on the wall instead of standing it up.' },
        { slug: 'inhaker-koppelstuk', label: 'Connector', text: 'Fixes a post to floor, ceiling, wall or another post.' },
        { slug: 'inhaker-haken-met-plakstrip', label: 'Hooks with adhesive strip', text: 'For a panel without milled hooks: stick on and hang.' },
        { slug: 'inhaker-inslagdop', label: 'End cap', text: 'Neatly finishes the visible top of a short post.' },
      ],
      faqTitle: 'Inhaker system FAQ',
      faqs: [
        { q: 'What do I need at minimum?', a: 'For a freestanding sign: two posts, two base plates and a printed panel that hangs between the posts. Every post already comes with a connector to fix it to the base plate.' },
        { q: 'How thick can the panels be?', a: 'The post holds panels up to 10 mm thick. Our printed Inhaker panels are 10 mm Re-board or Forex with the hooks already milled.' },
        { q: 'My panel has no hooks. Can I still use it?', a: 'Yes, with the hooks with adhesive strip. Stick them at a right angle to the back of the panel so it fits into the post.' },
        { q: 'Can I use the Inhaker outdoors?', a: 'The posts are water repellent but made for indoor use. For outdoors we recommend a sign board or facade sign.' },
        { q: 'Can I fix a post to the wall?', a: 'Yes. The wall hook with lock hangs the post on the wall, and the connector fixes it to the floor or ceiling.' },
        { q: 'Can I order as a business?', a: 'Yes. You get an invoice in your company name, and within the EU the VAT is reverse charged with a valid VAT number.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'borden', label: 'Sign boards' },
        { id: 'kerst', label: 'Christmas' },
      ],
    },
    fr: {
      pathKey: '/inhaker-systeem',
      metaTitle: 'Système Inhaker : poteaux, platines et panneaux imprimés | Printen.eu',
      metaDescription: 'Créez votre présentoir, enseigne, signalétique ou stand avec le système Inhaker. Poteaux noirs, platines et panneaux imprimés à accrocher. Commandez en ligne.',
      badge: 'Présentoir, enseigne ou stand avec un seul système',
      title: 'Le système Inhaker : poteaux et panneaux qui s’accrochent',
      subtitle: 'Un Inhaker est un poteau noir dans lequel vous accrochez des panneaux imprimés jusqu’à 10 mm d’épaisseur. Avec quelques poteaux, platines et panneaux, vous créez un présentoir, une enseigne, une signalétique, une cloison autoportante ou un stand complet, et vous le transformez tout aussi facilement.',
      primaryCta: 'Voir le poteau Inhaker',
      primaryHref: '/producten/inhaker-staander',
      support: 'Vous hésitez sur les pièces nécessaires ? Envoyez votre plan ou croquis à support@printen.eu, nous vous aidons à calculer.',
      usps: [
        ['Accrocher, pas visser', 'Les panneaux à crochets fraisés s’accrochent dans le poteau sans outil.'],
        ['Sept hauteurs', 'Poteaux de 45 cm pour un comptoir à 300 cm pour une cloison ou un stand.'],
        ['Réutilisable', 'Pour une nouvelle action ou un salon, changez seulement les panneaux ; les poteaux restent.'],
        ['Matériau recyclé', 'Les poteaux sont en PVC recyclé de haute qualité.'],
      ],
      productsTitle: 'Les pièces du système',
      products: [
        { slug: 'inhaker-staander', label: 'Poteau Inhaker', text: 'La base : poteau noir en sept hauteurs, raccord inclus.' },
        { slug: 'inhaker-voetplaat', label: 'Platine', text: 'Pied en acier pour poser le poteau librement dans la pièce.' },
        { slug: 'inhaker-plaatmateriaal', label: 'Panneaux imprimés', text: 'Re-board ou Forex 10 mm à crochets fraisés, en formes fixes.' },
        { slug: 'inhaker-beursstand', label: 'Stand complet', text: 'Cloison imprimée de 88 cm de large en trois hauteurs, prête à monter.' },
        { slug: 'inhaker-kerstboom', label: 'Sapin de Noël', text: 'Sapin imprimé de 127,5 x 168 cm pour vitrine ou bureau.' },
        { slug: 'inhaker-wandhaak', label: 'Crochet mural avec verrou', text: 'Accrochez un poteau au mur au lieu de le poser.' },
        { slug: 'inhaker-koppelstuk', label: 'Raccord', text: 'Fixe un poteau au sol, au plafond, au mur ou à un autre poteau.' },
        { slug: 'inhaker-haken-met-plakstrip', label: 'Crochets avec bande adhésive', text: 'Pour un panneau sans crochets fraisés : collez et accrochez.' },
        { slug: 'inhaker-inslagdop', label: 'Embout', text: 'Finit proprement le haut visible d’un poteau court.' },
      ],
      faqTitle: 'Questions fréquentes sur le système Inhaker',
      faqs: [
        { q: 'De quoi ai-je besoin au minimum ?', a: 'Pour une enseigne autoportante : deux poteaux, deux platines et un panneau imprimé accroché entre les poteaux. Chaque poteau est déjà livré avec un raccord pour la platine.' },
        { q: 'Quelle épaisseur pour les panneaux ?', a: 'Le poteau accepte des panneaux jusqu’à 10 mm. Nos panneaux Inhaker imprimés sont en Re-board ou Forex 10 mm, avec crochets déjà fraisés.' },
        { q: 'Mon panneau n’a pas de crochets. Est-ce possible ?', a: 'Oui, avec les crochets avec bande adhésive. Collez-les perpendiculairement au dos du panneau pour qu’il s’accroche dans le poteau.' },
        { q: 'Puis-je utiliser l’Inhaker à l’extérieur ?', a: 'Les poteaux sont déperlants mais prévus pour l’intérieur. Pour l’extérieur, nous conseillons un panneau publicitaire ou une enseigne de façade.' },
        { q: 'Puis-je fixer un poteau au mur ?', a: 'Oui. Le crochet mural avec verrou accroche le poteau au mur, et le raccord le fixe au sol ou au plafond.' },
        { q: 'Puis-je commander en tant qu’entreprise ?', a: 'Oui. Vous recevez une facture au nom de votre entreprise, et dans l’UE la TVA est autoliquidée avec un numéro de TVA valide.' },
      ],
      relatedTitle: 'À voir aussi',
      related: [
        { id: 'borden', label: 'Panneaux publicitaires' },
        { id: 'kerst', label: 'Noël' },
      ],
    },
  },
  drukwerk: {
    nl: {
      pathKey: '/drukwerk',
      metaTitle: 'Drukwerk bestellen: flyers, folders, brochures en ansichtkaarten | Printen.eu',
      metaDescription: 'Flyers, folders, ansichtkaarten, brochures, magazines en kranten drukken in je eigen formaat. Van A7 tot A3, geniet, wire-o of gelijmd. Gratis bestandscontrole.',
      badge: 'Van een kaartje tot een boekwerk van 500 pagina’s',
      title: 'Drukwerk bestellen in elk formaat',
      subtitle: 'Flyers en folders voor een actie, ansichtkaarten voor je klanten, of een brochure, magazine of krant voor je verhaal. Kies formaat, papier en afwerking, upload je ontwerp en bestel direct.',
      primaryCta: 'Flyers drukken',
      primaryHref: '/producten/flyers',
      support: 'Twijfel je over formaat, papier of aantal pagina’s? Mail ons via support@printen.eu.',
      usps: [
        ['Elk formaat', 'Van A7 tot A3, vierkant, langwerpig of je eigen maat.'],
        ['Drie bindwijzen', 'Geniet tot 96 pagina’s, wire-o tot 480 en gelijmd tot 564 pagina’s.'],
        ['Papier naar keuze', 'Van dun en voordelig tot dik karton, glanzend of mat.'],
        ['Gratis bestandscontrole', 'Wij kijken je bestand na voordat het gedrukt wordt.'],
      ],
      productsTitle: 'Kies je drukwerk',
      products: [
        { slug: 'flyers', label: 'Flyers', text: 'Enkel- of dubbelzijdig, van A7 tot A2.' },
        { slug: 'folder', label: 'Folders', text: 'Enkele vouw, wikkel-, zigzag- of luikvouw, van A7 tot A3.' },
        { slug: 'ansichtkaarten', label: 'Ansichtkaarten', text: 'Van A7 tot A3 en vierkant, ook langwerpig.' },
        { slug: 'magazine-geniet', label: 'Geniete brochure of magazine', text: 'Met nietjes in de rug, van 8 tot 96 pagina’s.' },
        { slug: 'brochure-wire-o', label: 'Wire-o brochure', text: 'Met ringband, valt plat open, van 6 tot 480 pagina’s.' },
        { slug: 'brochure-gelijmd', label: 'Gelijmde brochure', text: 'Met strakke rug, van 32 tot 564 pagina’s.' },
        { slug: 'kranten', label: 'Kranten', text: 'Tabloid of half berliner, in full colour.' },
        { slug: 'visitekaartjes', label: 'Visitekaartjes', text: '85 x 55 mm of je eigen formaat, met luxe afwerking.' },
        { slug: 'posters', label: 'Posters', text: 'Van A4 tot abri-formaat van 118,5 x 175 cm.' },
      ],
      faqTitle: 'Veelgestelde vragen over drukwerk',
      faqs: [
        { q: 'Wat is de afmeting van een ansichtkaart?', a: 'De meest gebruikte maat is A6: 10,5 x 14,8 cm. Wil je groter, dan is A5 (14,8 x 21 cm) populair. Wij drukken ansichtkaarten ook als langwerpige kaart van 9,8 x 21 cm en vierkant, bijvoorbeeld 14,8 x 14,8 cm.' },
        { q: 'Geniet, wire-o of gelijmd: wat kies ik?', a: 'Nietjes zijn voordelig voor dunne brochures tot 96 pagina’s. Wire-o valt helemaal plat open, handig voor kalenders, handleidingen en receptenboekjes. Gelijmd geeft een strakke rug zoals een boek en is er vanaf 32 pagina’s.' },
        { q: 'Welke maten zijn A-formaten?', a: 'A4 is 21 x 29,7 cm, A5 is de helft (14,8 x 21 cm), A6 weer de helft (10,5 x 14,8 cm) en A3 het dubbele van A4 (29,7 x 42 cm).' },
        { q: 'Welk bestand moet ik aanleveren?', a: 'Een PDF op het gekozen formaat met 3 mm afloop rondom werkt het best. Houd tekst minstens 3 mm van de snijrand. Wij controleren je bestand gratis voordat het gedrukt wordt.' },
        { q: 'Kan ik als bedrijf bestellen?', a: 'Ja. Je krijgt een factuur op bedrijfsnaam, en binnen de EU met een geldig btw-nummer wordt de btw verlegd.' },
        { q: 'Wat als er iets mis is met de druk?', a: 'Mail een foto en je ordernummer naar support@printen.eu. Bij een drukfout of transportschade drukken we opnieuw of krijg je je geld terug.' },
      ],
      relatedTitle: 'Ook handig',
      related: [
        { id: 'kerst', label: 'Kerstkaarten' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    de: {
      pathKey: '/drukwerk',
      metaTitle: 'Drucksachen bestellen: Flyer, Faltblätter, Broschüren und Postkarten | Printen.eu',
      metaDescription: 'Flyer, Faltblätter, Postkarten, Broschüren, Magazine und Zeitungen im eigenen Format drucken. Von A7 bis A3, geheftet, Wire-O oder klebegebunden. Dateiprüfung inklusive.',
      badge: 'Von der Karte bis zum Buch mit 500 Seiten',
      title: 'Drucksachen in jedem Format bestellen',
      subtitle: 'Flyer und Faltblätter für eine Aktion, Postkarten für Ihre Kunden oder eine Broschüre, ein Magazin oder eine Zeitung für Ihre Geschichte. Format, Papier und Veredelung wählen, Motiv hochladen und direkt bestellen.',
      primaryCta: 'Flyer drucken',
      primaryHref: '/producten/flyers',
      support: 'Unsicher bei Format, Papier oder Seitenzahl? Schreiben Sie uns per E-Mail an support@printen.eu.',
      usps: [
        ['Jedes Format', 'Von A7 bis A3, quadratisch, lang oder im eigenen Maß.'],
        ['Drei Bindungen', 'Geheftet bis 96 Seiten, Wire-O bis 480 und klebegebunden bis 564 Seiten.'],
        ['Papier nach Wahl', 'Von dünn und günstig bis zu festem Karton, glänzend oder matt.'],
        ['Kostenlose Dateiprüfung', 'Wir prüfen Ihre Datei vor dem Druck.'],
      ],
      productsTitle: 'Ihre Drucksache',
      products: [
        { slug: 'flyers', label: 'Flyer', text: 'Ein- oder beidseitig, von A7 bis A2.' },
        { slug: 'folder', label: 'Faltblätter', text: 'Einbruch-, Wickel-, Zickzack- oder Fensterfalz, von A7 bis A3.' },
        { slug: 'ansichtkaarten', label: 'Postkarten', text: 'Von A7 bis A3 und quadratisch, auch im Langformat.' },
        { slug: 'magazine-geniet', label: 'Geheftete Broschüre oder Magazin', text: 'Mit Rückenheftung, von 8 bis 96 Seiten.' },
        { slug: 'brochure-wire-o', label: 'Wire-O-Broschüre', text: 'Mit Ringbindung, liegt flach auf, von 6 bis 480 Seiten.' },
        { slug: 'brochure-gelijmd', label: 'Klebegebundene Broschüre', text: 'Mit sauberem Rücken, von 32 bis 564 Seiten.' },
        { slug: 'kranten', label: 'Zeitungen', text: 'Tabloid oder Halbberliner, vollfarbig.' },
        { slug: 'visitekaartjes', label: 'Visitenkarten', text: '85 x 55 mm oder eigenes Format, mit edler Veredelung.' },
        { slug: 'posters', label: 'Poster', text: 'Von A4 bis zum City-Light-Format 118,5 x 175 cm.' },
      ],
      faqTitle: 'Häufige Fragen zu Drucksachen',
      faqs: [
        { q: 'Welche Größe hat eine Postkarte?', a: 'Am häufigsten ist A6: 10,5 x 14,8 cm. Etwas größer ist A5 (14,8 x 21 cm) beliebt. Wir drucken Postkarten auch im Langformat 9,8 x 21 cm und quadratisch, zum Beispiel 14,8 x 14,8 cm.' },
        { q: 'Geheftet, Wire-O oder klebegebunden?', a: 'Heftung ist günstig für dünne Broschüren bis 96 Seiten. Wire-O liegt flach auf, praktisch für Kalender, Anleitungen und Rezepthefte. Klebebindung ergibt einen sauberen Rücken wie ein Buch, ab 32 Seiten.' },
        { q: 'Wie groß sind die A-Formate?', a: 'A4 misst 21 x 29,7 cm, A5 ist die Hälfte (14,8 x 21 cm), A6 wiederum die Hälfte (10,5 x 14,8 cm) und A3 das Doppelte von A4 (29,7 x 42 cm).' },
        { q: 'Welche Datei soll ich anliefern?', a: 'Am besten ein PDF im gewählten Format mit 3 mm Beschnitt rundum. Halten Sie Text mindestens 3 mm vom Schnittrand fern. Wir prüfen Ihre Datei kostenlos vor dem Druck.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Sie erhalten eine Rechnung auf Ihren Firmennamen, und innerhalb der EU wird die Mehrwertsteuer mit gültiger USt-IdNr. umgekehrt.' },
        { q: 'Was, wenn mit dem Druck etwas nicht stimmt?', a: 'Schicken Sie ein Foto und Ihre Bestellnummer per E-Mail an support@printen.eu. Bei einem Druckfehler oder Transportschaden drucken wir neu oder erstatten den Betrag.' },
      ],
      relatedTitle: 'Auch interessant',
      related: [
        { id: 'kerst', label: 'Weihnachtskarten' },
        { id: 'stickers', label: 'Aufkleber' },
      ],
    },
    en: {
      pathKey: '/drukwerk',
      metaTitle: 'Order printed matter: flyers, leaflets, brochures and postcards | Printen.eu',
      metaDescription: 'Print flyers, folded leaflets, postcards, brochures, magazines and newspapers in your own size. From A7 to A3, stapled, wire-o or perfect bound. Free file check.',
      badge: 'From a single card to a 500 page book',
      title: 'Order printed matter in any size',
      subtitle: 'Flyers and leaflets for a campaign, postcards for your customers, or a brochure, magazine or newspaper to tell your story. Choose size, paper and finish, upload your design and order straight away.',
      primaryCta: 'Print flyers',
      primaryHref: '/producten/flyers',
      support: 'Not sure about size, paper or number of pages? Email us at support@printen.eu.',
      usps: [
        ['Any size', 'From A7 to A3, square, long or your own size.'],
        ['Three bindings', 'Stapled up to 96 pages, wire-o up to 480 and perfect bound up to 564 pages.'],
        ['Your choice of paper', 'From thin and affordable to heavy board, gloss or matt.'],
        ['Free file check', 'We check your file before it is printed.'],
      ],
      productsTitle: 'Choose your print',
      products: [
        { slug: 'flyers', label: 'Flyers', text: 'Single or double sided, from A7 to A2.' },
        { slug: 'folder', label: 'Folded leaflets', text: 'Single, roll, zigzag or gate fold, from A7 to A3.' },
        { slug: 'ansichtkaarten', label: 'Postcards', text: 'From A7 to A3 and square, also in long formats.' },
        { slug: 'magazine-geniet', label: 'Stapled brochure or magazine', text: 'Saddle stitched, from 8 to 96 pages.' },
        { slug: 'brochure-wire-o', label: 'Wire-o brochure', text: 'Ring bound, lies flat, from 6 to 480 pages.' },
        { slug: 'brochure-gelijmd', label: 'Perfect bound brochure', text: 'With a clean spine, from 32 to 564 pages.' },
        { slug: 'kranten', label: 'Newspapers', text: 'Tabloid or half Berliner, full colour.' },
        { slug: 'visitekaartjes', label: 'Business cards', text: '85 x 55 mm or your own size, with premium finishes.' },
        { slug: 'posters', label: 'Posters', text: 'From A4 up to bus shelter size, 118.5 x 175 cm.' },
      ],
      faqTitle: 'Printed matter FAQ',
      faqs: [
        { q: 'What size is a postcard?', a: 'The most common size is A6: 10.5 x 14.8 cm. For something larger, A5 (14.8 x 21 cm) is popular. We also print long postcards of 9.8 x 21 cm and square ones, for example 14.8 x 14.8 cm.' },
        { q: 'Stapled, wire-o or perfect bound?', a: 'Staples are affordable for thin brochures up to 96 pages. Wire-o opens completely flat, handy for calendars, manuals and recipe books. Perfect binding gives a clean spine like a book and starts at 32 pages.' },
        { q: 'What sizes are the A formats?', a: 'A4 is 21 x 29.7 cm, A5 is half of that (14.8 x 21 cm), A6 half again (10.5 x 14.8 cm) and A3 double A4 (29.7 x 42 cm).' },
        { q: 'Which file should I supply?', a: 'A PDF at the chosen size with 3 mm bleed all round works best. Keep text at least 3 mm from the trim edge. We check your file for free before printing.' },
        { q: 'Can I order as a business?', a: 'Yes. You get an invoice in your company name, and within the EU the VAT is reverse charged with a valid VAT number.' },
        { q: 'What if something is wrong with the print?', a: 'Email a photo and your order number to support@printen.eu. For a print fault or transit damage we reprint or refund.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'kerst', label: 'Christmas cards' },
        { id: 'stickers', label: 'Stickers' },
      ],
    },
    fr: {
      pathKey: '/drukwerk',
      metaTitle: 'Imprimés : flyers, dépliants, brochures et cartes postales | Printen.eu',
      metaDescription: 'Imprimez flyers, dépliants, cartes postales, brochures, magazines et journaux au format de votre choix. Du A7 au A3, agrafé, wire-o ou dos collé. Vérification gratuite.',
      badge: 'De la carte au livre de 500 pages',
      title: 'Commandez vos imprimés dans tous les formats',
      subtitle: 'Flyers et dépliants pour une action, cartes postales pour vos clients, ou une brochure, un magazine ou un journal pour raconter votre histoire. Choisissez format, papier et finition, envoyez votre visuel et commandez.',
      primaryCta: 'Imprimer des flyers',
      primaryHref: '/producten/flyers',
      support: 'Un doute sur le format, le papier ou le nombre de pages ? Écrivez-nous à support@printen.eu.',
      usps: [
        ['Tous les formats', 'Du A7 au A3, carré, allongé ou sur mesure.'],
        ['Trois reliures', 'Agrafé jusqu’à 96 pages, wire-o jusqu’à 480 et dos collé jusqu’à 564 pages.'],
        ['Papier au choix', 'Du papier fin et économique au carton épais, brillant ou mat.'],
        ['Vérification gratuite', 'Nous vérifions votre fichier avant l’impression.'],
      ],
      productsTitle: 'Choisissez votre imprimé',
      products: [
        { slug: 'flyers', label: 'Flyers', text: 'Recto ou recto verso, du A7 au A2.' },
        { slug: 'folder', label: 'Dépliants', text: 'Pli simple, roulé, accordéon ou fenêtre, du A7 au A3.' },
        { slug: 'ansichtkaarten', label: 'Cartes postales', text: 'Du A7 au A3 et carrées, aussi en format long.' },
        { slug: 'magazine-geniet', label: 'Brochure ou magazine agrafé', text: 'Piqûre à cheval, de 8 à 96 pages.' },
        { slug: 'brochure-wire-o', label: 'Brochure wire-o', text: 'Reliure à anneaux, s’ouvre à plat, de 6 à 480 pages.' },
        { slug: 'brochure-gelijmd', label: 'Brochure dos collé', text: 'Avec un dos net, de 32 à 564 pages.' },
        { slug: 'kranten', label: 'Journaux', text: 'Tabloïd ou demi-berlinois, en couleur.' },
        { slug: 'visitekaartjes', label: 'Cartes de visite', text: '85 x 55 mm ou sur mesure, avec finitions de luxe.' },
        { slug: 'posters', label: 'Affiches', text: 'Du A4 au format abribus de 118,5 x 175 cm.' },
      ],
      faqTitle: 'Questions fréquentes sur les imprimés',
      faqs: [
        { q: 'Quelle est la taille d’une carte postale ?', a: 'Le format le plus courant est le A6 : 10,5 x 14,8 cm. Un peu plus grand, le A5 (14,8 x 21 cm) est apprécié. Nous imprimons aussi des cartes longues de 9,8 x 21 cm et carrées, par exemple 14,8 x 14,8 cm.' },
        { q: 'Agrafé, wire-o ou dos collé ?', a: 'L’agrafage est économique pour les brochures fines jusqu’à 96 pages. Le wire-o s’ouvre complètement à plat, idéal pour calendriers, manuels et livres de recettes. Le dos collé donne un dos net comme un livre, à partir de 32 pages.' },
        { q: 'Quelles sont les dimensions des formats A ?', a: 'Le A4 mesure 21 x 29,7 cm, le A5 la moitié (14,8 x 21 cm), le A6 encore la moitié (10,5 x 14,8 cm) et le A3 le double du A4 (29,7 x 42 cm).' },
        { q: 'Quel fichier fournir ?', a: 'Un PDF au format choisi avec 3 mm de fond perdu tout autour. Gardez le texte à au moins 3 mm du bord de coupe. Nous vérifions votre fichier gratuitement avant l’impression.' },
        { q: 'Puis-je commander en tant qu’entreprise ?', a: 'Oui. Vous recevez une facture au nom de votre entreprise, et dans l’UE la TVA est autoliquidée avec un numéro de TVA valide.' },
        { q: 'Et si l’impression pose problème ?', a: 'Envoyez une photo et votre numéro de commande à support@printen.eu. En cas de défaut d’impression ou de dommage au transport, nous réimprimons ou remboursons.' },
      ],
      relatedTitle: 'À voir aussi',
      related: [
        { id: 'kerst', label: 'Cartes de Noël' },
        { id: 'stickers', label: 'Autocollants' },
      ],
    },
  },
  beurs: {
    nl: {
      pathKey: '/beurs-en-evenement',
      metaTitle: 'Beurs en evenement: beachflags, roll-ups en beurswanden | Printen.eu',
      metaDescription: 'Alles voor je beursstand of evenement: beachflags, roll-up banners, pop-up wanden, beursframes, balies en tafelkleden met je eigen ontwerp. Bestel online.',
      badge: 'Voor beurzen, events en je eigen winkel',
      title: 'Opvallen op de beurs of je evenement',
      subtitle: 'Van een beachflag bij de ingang tot een complete beurswand met balie. Kies wat bij je stand past, upload je ontwerp en neem alles herbruikbaar mee naar de volgende beurs.',
      primaryCta: 'Beachflags bekijken',
      primaryHref: '/producten/beachflags',
      support: 'Een complete stand inrichten? Mail je plattegrond of wensen naar support@printen.eu en we denken mee.',
      usps: [
        ['Herbruikbaar', 'Frames, cassettes en masten gaan mee naar de volgende beurs; vervang alleen de print.'],
        ['Snel opgezet', 'Roll-ups, pop-up wanden en balies zet je op zonder gereedschap.'],
        ['Van klein tot groot', 'Van een tafelvlag tot een beurswand van 4,45 meter breed.'],
        ['Gratis bestandscontrole', 'Wij kijken je bestand na voordat het gedrukt wordt.'],
      ],
      productsTitle: 'Alles voor je stand',
      products: [
        { slug: 'beachflags', label: 'Beachflags', text: 'Recht, schuin, hol, square of drop, van 2 tot 4,4 meter hoog.' },
        { slug: 'roll-up-banner', label: 'Roll-up banner', text: 'Van 85 tot 200 cm breed, met cassette en draagtas.' },
        { slug: 'x-banner', label: 'X-banner', text: '80 x 200 cm, als complete set of alleen het doek.' },
        { slug: 'pop-up-wand', label: 'Pop-up wand', text: 'Recht of gebogen, tot 4,45 meter breed, optioneel met LED.' },
        { slug: 'beursframe', label: 'Beursframe', text: 'Aluminium frame van 2, 3 of 4 meter breed met bedrukt doek.' },
        { slug: 'backdrop', label: 'Backdrop', text: 'Bedrukt podium- of fotowanddoek in elk formaat tot 3 x 5 meter.' },
        { slug: 'beursbalie-square', label: 'Beursbalie', text: 'Compacte balie met verwisselbare print en transporttas.' },
        { slug: 'tafelkleed', label: 'Tafelkleed', text: 'Bedrukt tafelkleed in standaardformaat of op maat.' },
        { slug: 'banier', label: 'Banier', text: 'Voor binnen of aan de gevel, vanaf 60 cm breed.' },
      ],
      faqTitle: 'Veelgestelde vragen over beurs- en eventmateriaal',
      faqs: [
        { q: 'Welke beachflag kies ik?', a: 'Een rechte of square flag heeft het meeste ruimte voor tekst. Een holle of dropflag valt op door de vorm. Hoe groter de vlag, hoe verder weg hij zichtbaar is; de grootste is ongeveer 4,4 meter hoog.' },
        { q: 'Roll-up of X-banner?', a: 'Een roll-up rolt in een cassette, is in een minuut opgezet en blijft netjes bij vervoer. Een X-banner is lichter en voordeliger, met een spanframe achter het doek.' },
        { q: 'Kan ik alleen een nieuwe print bestellen?', a: 'Ja, voor onder meer het beursframe en de X-banner kun je alleen het doek bestellen, zodat je je bestaande frame opnieuw gebruikt.' },
        { q: 'Hoe lever ik mijn ontwerp aan?', a: 'Een PDF op het gekozen formaat met afloop werkt het best. In de configurator zie je per product de juiste maat. Wij controleren je bestand gratis voordat het gedrukt wordt.' },
        { q: 'Kan ik als bedrijf bestellen?', a: 'Ja. Je krijgt een factuur op bedrijfsnaam, en binnen de EU met een geldig btw-nummer wordt de btw verlegd.' },
        { q: 'Wat als er iets mis is met de druk?', a: 'Mail een foto en je ordernummer naar support@printen.eu. Bij een drukfout of transportschade drukken we opnieuw of krijg je je geld terug.' },
      ],
      relatedTitle: 'Ook handig',
      related: [
        { id: 'inhaker', label: 'Inhaker systeem' },
        { id: 'borden', label: 'Reclameborden' },
      ],
    },
    de: {
      pathKey: '/beurs-en-evenement',
      metaTitle: 'Messe und Event: Beachflags, Roll-ups und Messewände | Printen.eu',
      metaDescription: 'Alles für Ihren Messestand oder Ihr Event: Beachflags, Roll-up-Banner, Pop-up-Wände, Messerahmen, Theken und Tischdecken mit eigenem Motiv. Online bestellen.',
      badge: 'Für Messen, Events und Ihr eigenes Geschäft',
      title: 'Auffallen auf der Messe oder Ihrem Event',
      subtitle: 'Von der Beachflag am Eingang bis zur kompletten Messewand mit Theke. Wählen Sie, was zu Ihrem Stand passt, laden Sie Ihr Motiv hoch und nehmen Sie alles wiederverwendbar zur nächsten Messe mit.',
      primaryCta: 'Beachflags ansehen',
      primaryHref: '/producten/beachflags',
      support: 'Einen kompletten Stand einrichten? Schicken Sie Ihren Grundriss oder Ihre Wünsche per E-Mail an support@printen.eu.',
      usps: [
        ['Wiederverwendbar', 'Rahmen, Kassetten und Masten kommen zur nächsten Messe mit; nur der Druck wird getauscht.'],
        ['Schnell aufgebaut', 'Roll-ups, Pop-up-Wände und Theken bauen Sie ohne Werkzeug auf.'],
        ['Von klein bis groß', 'Von der Tischflagge bis zur Messewand mit 4,45 Metern Breite.'],
        ['Kostenlose Dateiprüfung', 'Wir prüfen Ihre Datei vor dem Druck.'],
      ],
      productsTitle: 'Alles für Ihren Stand',
      products: [
        { slug: 'beachflags', label: 'Beachflags', text: 'Gerade, schräg, hohl, Square oder Drop, 2 bis 4,4 Meter hoch.' },
        { slug: 'roll-up-banner', label: 'Roll-up-Banner', text: '85 bis 200 cm breit, mit Kassette und Tragetasche.' },
        { slug: 'x-banner', label: 'X-Banner', text: '80 x 200 cm, als Komplettset oder nur der Druck.' },
        { slug: 'pop-up-wand', label: 'Pop-up-Wand', text: 'Gerade oder gebogen, bis 4,45 Meter breit, optional mit LED.' },
        { slug: 'beursframe', label: 'Messerahmen', text: 'Aluminiumrahmen, 2, 3 oder 4 Meter breit, mit bedrucktem Stoff.' },
        { slug: 'backdrop', label: 'Backdrop', text: 'Bedruckter Bühnen- oder Fotowandstoff in jedem Format bis 3 x 5 Meter.' },
        { slug: 'beursbalie-square', label: 'Messetheke', text: 'Kompakte Theke mit wechselbarem Druck und Transporttasche.' },
        { slug: 'tafelkleed', label: 'Tischdecke', text: 'Bedruckte Tischdecke im Standardformat oder nach Maß.' },
        { slug: 'banier', label: 'Banner', text: 'Für innen oder die Fassade, ab 60 cm Breite.' },
      ],
      faqTitle: 'Häufige Fragen zu Messe- und Eventmaterial',
      faqs: [
        { q: 'Welche Beachflag wähle ich?', a: 'Eine gerade oder Square-Flag bietet den meisten Platz für Text. Eine hohle oder Drop-Flag fällt durch ihre Form auf. Je größer die Flagge, desto weiter ist sie zu sehen; die größte ist etwa 4,4 Meter hoch.' },
        { q: 'Roll-up oder X-Banner?', a: 'Ein Roll-up rollt sich in eine Kassette, steht in einer Minute und bleibt beim Transport sauber. Ein X-Banner ist leichter und günstiger, mit einem Spannrahmen hinter dem Stoff.' },
        { q: 'Kann ich nur einen neuen Druck bestellen?', a: 'Ja, unter anderem beim Messerahmen und beim X-Banner können Sie nur den Druck bestellen und Ihren vorhandenen Rahmen weiterverwenden.' },
        { q: 'Wie liefere ich mein Motiv an?', a: 'Am besten ein PDF im gewählten Format mit Beschnitt. Im Konfigurator sehen Sie pro Produkt die richtige Größe. Wir prüfen Ihre Datei kostenlos vor dem Druck.' },
        { q: 'Kann ich als Firma bestellen?', a: 'Ja. Sie erhalten eine Rechnung auf Ihren Firmennamen, und innerhalb der EU wird die Mehrwertsteuer mit gültiger USt-IdNr. umgekehrt.' },
        { q: 'Was, wenn mit dem Druck etwas nicht stimmt?', a: 'Schicken Sie ein Foto und Ihre Bestellnummer per E-Mail an support@printen.eu. Bei einem Druckfehler oder Transportschaden drucken wir neu oder erstatten den Betrag.' },
      ],
      relatedTitle: 'Auch interessant',
      related: [
        { id: 'inhaker', label: 'Inhaker System' },
        { id: 'borden', label: 'Werbeschilder' },
      ],
    },
    en: {
      pathKey: '/beurs-en-evenement',
      metaTitle: 'Trade shows and events: beach flags, roll-ups and display walls | Printen.eu',
      metaDescription: 'Everything for your trade show stand or event: beach flags, roll-up banners, pop-up walls, exhibition frames, counters and tablecloths with your own design. Order online.',
      badge: 'For trade shows, events and your own shop',
      title: 'Stand out at the trade show or your event',
      subtitle: 'From a beach flag at the entrance to a complete display wall with counter. Choose what suits your stand, upload your design and take everything reusable to the next show.',
      primaryCta: 'See beach flags',
      primaryHref: '/producten/beachflags',
      support: 'Fitting out a complete stand? Email your floor plan or wishes to support@printen.eu and we will help.',
      usps: [
        ['Reusable', 'Frames, cassettes and poles go with you to the next show; only replace the print.'],
        ['Quick to set up', 'Roll-ups, pop-up walls and counters go up without tools.'],
        ['Small to large', 'From a table flag to a display wall 4.45 metres wide.'],
        ['Free file check', 'We check your file before it is printed.'],
      ],
      productsTitle: 'Everything for your stand',
      products: [
        { slug: 'beachflags', label: 'Beach flags', text: 'Straight, angled, curved, square or drop, from 2 to 4.4 metres high.' },
        { slug: 'roll-up-banner', label: 'Roll-up banner', text: 'From 85 to 200 cm wide, with cassette and carry bag.' },
        { slug: 'x-banner', label: 'X-banner', text: '80 x 200 cm, as a complete set or the print only.' },
        { slug: 'pop-up-wand', label: 'Pop-up wall', text: 'Straight or curved, up to 4.45 metres wide, optional LED.' },
        { slug: 'beursframe', label: 'Exhibition frame', text: 'Aluminium frame 2, 3 or 4 metres wide with printed fabric.' },
        { slug: 'backdrop', label: 'Backdrop', text: 'Printed stage or photo wall fabric in any size up to 3 x 5 metres.' },
        { slug: 'beursbalie-square', label: 'Exhibition counter', text: 'Compact counter with replaceable print and carry bag.' },
        { slug: 'tafelkleed', label: 'Tablecloth', text: 'Printed tablecloth in a standard size or made to measure.' },
        { slug: 'banier', label: 'Banner flag', text: 'For indoors or the facade, from 60 cm wide.' },
      ],
      faqTitle: 'Trade show and event FAQ',
      faqs: [
        { q: 'Which beach flag should I choose?', a: 'A straight or square flag has the most room for text. A curved or drop flag stands out through its shape. The bigger the flag, the further away it is visible; the largest is about 4.4 metres high.' },
        { q: 'Roll-up or X-banner?', a: 'A roll-up rolls into a cassette, goes up in a minute and stays tidy in transport. An X-banner is lighter and more affordable, with a tension frame behind the fabric.' },
        { q: 'Can I order just a new print?', a: 'Yes, for products such as the exhibition frame and the X-banner you can order only the print and reuse your existing frame.' },
        { q: 'How do I supply my design?', a: 'A PDF at the chosen size with bleed works best. The configurator shows the right size for each product. We check your file for free before printing.' },
        { q: 'Can I order as a business?', a: 'Yes. You get an invoice in your company name, and within the EU the VAT is reverse charged with a valid VAT number.' },
        { q: 'What if something is wrong with the print?', a: 'Email a photo and your order number to support@printen.eu. For a print fault or transit damage we reprint or refund.' },
      ],
      relatedTitle: 'Also see',
      related: [
        { id: 'inhaker', label: 'Inhaker system' },
        { id: 'borden', label: 'Sign boards' },
      ],
    },
    fr: {
      pathKey: '/beurs-en-evenement',
      metaTitle: 'Salons et événements : beach flags, roll-ups et murs d’exposition | Printen.eu',
      metaDescription: 'Tout pour votre stand ou votre événement : beach flags, roll-ups, murs pop-up, cadres d’exposition, comptoirs et nappes avec votre visuel. Commandez en ligne.',
      badge: 'Pour salons, événements et votre boutique',
      title: 'Faites-vous remarquer au salon ou à votre événement',
      subtitle: 'Du beach flag à l’entrée au mur d’exposition complet avec comptoir. Choisissez ce qui convient à votre stand, envoyez votre visuel et réutilisez tout au prochain salon.',
      primaryCta: 'Voir les beach flags',
      primaryHref: '/producten/beachflags',
      support: 'Vous aménagez un stand complet ? Envoyez votre plan ou vos souhaits à support@printen.eu.',
      usps: [
        ['Réutilisable', 'Cadres, cassettes et mâts vous suivent au prochain salon ; seule l’impression change.'],
        ['Montage rapide', 'Roll-ups, murs pop-up et comptoirs se montent sans outil.'],
        ['Du petit au grand', 'Du drapeau de table au mur d’exposition de 4,45 mètres de large.'],
        ['Vérification gratuite', 'Nous vérifions votre fichier avant l’impression.'],
      ],
      productsTitle: 'Tout pour votre stand',
      products: [
        { slug: 'beachflags', label: 'Beach flags', text: 'Droit, incliné, creux, square ou goutte, de 2 à 4,4 mètres de haut.' },
        { slug: 'roll-up-banner', label: 'Roll-up', text: 'De 85 à 200 cm de large, avec cassette et sac de transport.' },
        { slug: 'x-banner', label: 'X-banner', text: '80 x 200 cm, en kit complet ou impression seule.' },
        { slug: 'pop-up-wand', label: 'Mur pop-up', text: 'Droit ou courbé, jusqu’à 4,45 mètres de large, LED en option.' },
        { slug: 'beursframe', label: 'Cadre d’exposition', text: 'Cadre aluminium de 2, 3 ou 4 mètres de large avec tissu imprimé.' },
        { slug: 'backdrop', label: 'Backdrop', text: 'Tissu imprimé pour scène ou mur photo, jusqu’à 3 x 5 mètres.' },
        { slug: 'beursbalie-square', label: 'Comptoir d’exposition', text: 'Comptoir compact avec impression interchangeable et sac de transport.' },
        { slug: 'tafelkleed', label: 'Nappe', text: 'Nappe imprimée en format standard ou sur mesure.' },
        { slug: 'banier', label: 'Bannière', text: 'Pour l’intérieur ou la façade, à partir de 60 cm de large.' },
      ],
      faqTitle: 'Questions fréquentes sur le matériel de salon',
      faqs: [
        { q: 'Quel beach flag choisir ?', a: 'Un drapeau droit ou square offre le plus de place pour le texte. Un drapeau creux ou en goutte attire l’œil par sa forme. Plus il est grand, plus il se voit de loin ; le plus grand mesure environ 4,4 mètres.' },
        { q: 'Roll-up ou X-banner ?', a: 'Un roll-up s’enroule dans une cassette, se monte en une minute et reste propre pendant le transport. Un X-banner est plus léger et plus économique, avec un cadre tendeur derrière le tissu.' },
        { q: 'Puis-je commander seulement une nouvelle impression ?', a: 'Oui, notamment pour le cadre d’exposition et le X-banner, vous pouvez commander l’impression seule et réutiliser votre cadre.' },
        { q: 'Comment fournir mon visuel ?', a: 'Un PDF au format choisi avec fond perdu. Le configurateur indique la bonne taille pour chaque produit. Nous vérifions votre fichier gratuitement avant l’impression.' },
        { q: 'Puis-je commander en tant qu’entreprise ?', a: 'Oui. Vous recevez une facture au nom de votre entreprise, et dans l’UE la TVA est autoliquidée avec un numéro de TVA valide.' },
        { q: 'Et si l’impression pose problème ?', a: 'Envoyez une photo et votre numéro de commande à support@printen.eu. En cas de défaut d’impression ou de dommage au transport, nous réimprimons ou remboursons.' },
      ],
      relatedTitle: 'À voir aussi',
      related: [
        { id: 'inhaker', label: 'Système Inhaker' },
        { id: 'borden', label: 'Panneaux publicitaires' },
      ],
    },
  },
};

export const MONEY_PAGE_IDS: MoneyPageId[] = ['stickers', 'canvas', 'borden', 'kerst', 'bouw', 'inhaker', 'drukwerk', 'beurs'];

export const MONEY_PAGE_HREF: Record<MoneyPageId, MoneyPageCopy['pathKey']> = {
  stickers: '/stickers',
  canvas: '/foto-op-canvas',
  borden: '/reclameborden',
  kerst: '/kerstkaarten',
  bouw: '/bouwborden',
  inhaker: '/inhaker-systeem',
  drukwerk: '/drukwerk',
  beurs: '/beurs-en-evenement',
};

/** Gelokaliseerde URL per taal, zonder locale-prefix. */
export const MONEY_PAGE_PATHS: Record<MoneyPageId, Record<'nl' | 'de' | 'en' | 'fr', string>> = {
  stickers: { nl: '/stickers', de: '/aufkleber', en: '/stickers', fr: '/autocollants' },
  canvas: { nl: '/foto-op-canvas', de: '/foto-auf-leinwand', en: '/photo-on-canvas', fr: '/photo-sur-toile' },
  borden: { nl: '/reclameborden', de: '/werbeschilder', en: '/sign-boards', fr: '/panneaux-publicitaires' },
  kerst: { nl: '/kerstkaarten', de: '/weihnachtskarten', en: '/christmas-cards', fr: '/cartes-de-noel' },
  bouw: { nl: '/bouwborden', de: '/bauschilder', en: '/construction-site-signs', fr: '/panneaux-de-chantier' },
  inhaker: { nl: '/inhaker-systeem', de: '/inhaker-system', en: '/inhaker-system', fr: '/systeme-inhaker' },
  drukwerk: { nl: '/drukwerk', de: '/drucksachen', en: '/printed-matter', fr: '/imprimes' },
  beurs: { nl: '/beurs-en-evenement', de: '/messe-und-event', en: '/trade-shows-and-events', fr: '/salons-et-evenements' },
};

const NAV_LABEL: Record<MoneyPageId, Record<'nl' | 'de' | 'en' | 'fr', string>> = {
  stickers: { nl: 'Stickers', de: 'Aufkleber', en: 'Stickers', fr: 'Autocollants' },
  canvas: { nl: 'Foto op canvas', de: 'Foto auf Leinwand', en: 'Photo on canvas', fr: 'Photo sur toile' },
  borden: { nl: 'Reclameborden', de: 'Werbeschilder', en: 'Sign boards', fr: 'Panneaux publicitaires' },
  kerst: { nl: 'Kerstkaarten', de: 'Weihnachtskarten', en: 'Christmas cards', fr: 'Cartes de Noël' },
  bouw: { nl: 'Bouwborden', de: 'Bauschilder', en: 'Construction site signs', fr: 'Panneaux de chantier' },
  inhaker: { nl: 'Inhaker systeem', de: 'Inhaker System', en: 'Inhaker system', fr: 'Système Inhaker' },
  drukwerk: { nl: 'Drukwerk', de: 'Drucksachen', en: 'Printed matter', fr: 'Imprimés' },
  beurs: { nl: 'Beurs en evenement', de: 'Messe und Event', en: 'Trade shows and events', fr: 'Salons et événements' },
};

function moneyPageLang(locale: string): 'nl' | 'de' | 'en' | 'fr' {
  return locale === 'de' || locale === 'en' || locale === 'fr' ? locale : 'nl';
}

export function getMoneyPage(id: MoneyPageId, locale: string): MoneyPageCopy {
  return pages[id][moneyPageLang(locale)];
}

export function moneyPagePath(id: MoneyPageId, locale: string): string {
  return MONEY_PAGE_PATHS[id][moneyPageLang(locale)];
}

export function moneyPageNavLabel(id: MoneyPageId, locale: string): string {
  return NAV_LABEL[id][moneyPageLang(locale)];
}

/** Eerste URL-segment naar pad per locale, voor de taalswitcher in de header. */
export function moneyPageLanguageSwitcherRoutes(): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const id of MONEY_PAGE_IDS) {
    const paths = MONEY_PAGE_PATHS[id];
    for (const loc of ['nl', 'de', 'en', 'fr'] as const) {
      out[paths[loc].replace(/^\//, '')] = paths;
    }
  }
  return out;
}
